<template>
  <div class="min-h-screen catalog-page-shell">
    <!-- Main Content -->
    <div class="space-y-6">
      <div v-if="selectedStoreId && selectedCatalog.length > 0" class="catalog-page-actions">
        <hs-button v-if="activeTab === 'products' || activeTab === 'services'" variant="secondary" icon="copy"
          @click="handleOpenCopyModal">
          Copy to stores
        </hs-button>
        <hs-button v-if="activeTab === 'inventory'" variant="primary" icon="send" @click="handleOpenRequestModal">
          Request transfer
        </hs-button>
      </div>

      <!-- No stores at all -->
      <div v-if="!selectedStoreId && !isLoadingStores && stores.length === 0" class="hs-empty-panel text-center">
        <div class="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
          <vue-feather type="shopping-bag" class="w-8 h-8 text-gray-400" />
        </div>
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
          No Stores Available
        </h3>
        <p class="text-gray-500 dark:text-gray-400">
          You don't have access to any stores yet
        </p>
      </div>

      <!-- Stores exist but none selected -->
      <div v-if="!selectedStoreId && !isLoadingStores && stores.length > 0" class="hs-empty-panel text-center">
        <div class="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
          <vue-feather type="shopping-bag" class="w-8 h-8 text-gray-400" />
        </div>
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Select a Store
        </h3>
        <p class="text-gray-500 dark:text-gray-400">
          Choose a store from the dropdown above to manage its catalog
        </p>
      </div>

      <!-- Store selected -->
      <div v-else class="space-y-6">
        <!-- Stats Cards + Bulk Actions Expander -->
        <div class="catalog-summary-grid">
          <div class="catalog-summary-card catalog-summary-card--blue">
            <div class="catalog-summary-card__glow"></div>
            <div class="catalog-summary-card__content">
              <div class="catalog-summary-card__icon bg-blue-100 dark:bg-blue-900">
                <vue-feather type="package" class="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div class="min-w-0">
                <p class="catalog-summary-card__label">
                  Total Items
                </p>
                <p class="catalog-summary-card__value">
                  {{ stats.totalItems }}
                </p>
              </div>
            </div>
          </div>

          <div class="catalog-summary-card catalog-summary-card--green">
            <div class="catalog-summary-card__glow"></div>
            <div class="catalog-summary-card__content">
              <div class="catalog-summary-card__icon bg-green-100 dark:bg-green-900">
                <vue-feather type="shopping-bag" class="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div class="min-w-0">
                <p class="catalog-summary-card__label">
                  Products
                </p>
                <p class="catalog-summary-card__value">
                  {{ stats.products }}
                </p>
              </div>
            </div>
          </div>

          <div class="catalog-summary-card catalog-summary-card--purple">
            <div class="catalog-summary-card__glow"></div>
            <div class="catalog-summary-card__content">
              <div class="catalog-summary-card__icon bg-purple-100 dark:bg-purple-900">
                <vue-feather type="settings" class="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div class="min-w-0">
                <p class="catalog-summary-card__label">
                  Services
                </p>
                <p class="catalog-summary-card__value">
                  {{ stats.services }}
                </p>
              </div>
            </div>
          </div>

          <!-- Bulk Actions Expander Card -->
          <hs-button variant="secondary"
            class="catalog-summary-card catalog-summary-card--amber catalog-summary-card--button"
            @click="showBulkActions = true">
            <div class="catalog-summary-card__glow"></div>
            <div class="catalog-summary-card__content">
              <div class="catalog-summary-card__icon bg-amber-100 dark:bg-amber-900">
                <vue-feather type="tool" class="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div class="min-w-0">
                <p class="catalog-summary-card__label">
                  Bulk Actions
                </p>
                <p class="catalog-summary-card__hint">
                  Sync catalog items or download catalog templates
                </p>
              </div>
            </div>
            <vue-feather type="arrow-up-right" class="catalog-summary-card__chevron" />
          </hs-button>
        </div>

        <!-- Catalog Manager (Tabs + Table) -->
        <div class="md:flex" :class="{ 'mr-96': showEditModal }">
          <div class="md:flex-1">
            <CatalogTabs :active-tab="activeTab" :search-query="searchQuery" @tab-change="handleTabChange"
              :sort-by="sortBy" :expiration-filter="expirationFilter" :loading="tabLoading" @search="handleSearch"
              @sort-change="handleSortChange" @expiration-filter-change="handleExpirationFilterChange">
              <template #actions>
                <hs-button v-if="activeTab === 'inventory' && selectedStoreId"
                  variant="secondary" icon="sliders" @click="showInventoryPolicy = true">
                  Inventory policy
                </hs-button>
                <hs-button v-if="activeTab === 'inventory' && inventoryCommitmentsEnabled && selectedStoreId && openDeficitCount"
                  variant="secondary" icon="clipboard" @click="openInventoryReconciliation">
                  Resolve shortages
                  <span class="catalog-deficit-count">{{ openDeficitCount }}</span>
                </hs-button>

                <!-- Group selector for transfers and transfer requests -->
                <div v-if="['transfers', 'transfer_requests'].includes(activeTab)" class="catalog-action-cluster">
                  <HSCustomSelect v-model="selectedGroupId" :options="groupSelectOptions" icon="users"
                    placeholder="Select Group" class="catalog-toolbar-custom-select" @change="handleGroupChange" />

                  <!-- Store filter for transfer requests -->
                  <HSCustomSelect v-if="activeTab === 'transfer_requests' && selectedGroupId"
                    v-model="transferRequestStoreFilter" :options="transferStoreSelectOptions" icon="home"
                    class="catalog-toolbar-custom-select" @change="getTransferRequests" />

                  <!-- Store filter for transfers -->
                  <HSCustomSelect v-if="activeTab === 'transfers' && selectedGroupId" v-model="transferStoreFilter"
                    :options="transferStoreSelectOptions" icon="home" class="catalog-toolbar-custom-select"
                    @change="getTransfers" />

                  <hs-tooltip text="Refresh data">
                    <hs-button variant="secondary" icon="refresh-cw" icon-only @click="refreshActiveList" />
                  </hs-tooltip>
                </div>

                <div v-if="activeTab === 'removals' && selectedStoreId" class="catalog-action-cluster">
                  <HSCustomSelect v-model="removalFilters.reasonId" :options="removalReasonSelectOptions" icon="filter"
                    class="catalog-toolbar-custom-select" @change="handleRemovalFilterChange" />

                  <HsDateInput v-model="removalFilters.startDate" label="From" placeholder="Start Date"
                    :max="removalFilters.endDate" @change="handleRemovalFilterChange" />

                  <HsDateInput v-model="removalFilters.endDate" label="To" placeholder="End Date"
                    :min="removalFilters.startDate" @change="handleRemovalFilterChange" />

                  <hs-tooltip text="Clear removal filters">
                    <hs-button variant="secondary" icon="x-circle" icon-only @click="clearRemovalFilters" />
                  </hs-tooltip>

                  <hs-tooltip text="Download removal report">
                    <hs-button variant="success" icon="download" icon-only :disabled="downloadingRemovalReport"
                      @click="downloadRemovalReport" />
                  </hs-tooltip>

                  <hs-tooltip text="Refresh data">
                    <hs-button variant="secondary" icon="refresh-cw" icon-only @click="refreshActiveList" />
                  </hs-tooltip>
                </div>

                <!-- Refresh button for other tabs -->
                <hs-tooltip v-if="
                  !['transfers', 'transfer_requests', 'removals'].includes(
                    activeTab,
                  ) && selectedStoreId
                " text="Refresh data">
                  <hs-button variant="secondary" icon="refresh-cw" icon-only @click="refreshActiveList" />
                </hs-tooltip>

                <hs-button v-if="
                  selectedStoreId &&
                  (activeTab === 'products' || activeTab === 'services')
                " icon="plus" @click="handleOpenModal">
                  Add
                  {{
                    activeTab === "products"
                      ? "Product"
                      : activeTab === "services"
                        ? "Service"
                        : "Product"
                  }}
                </hs-button>
              </template>
            </CatalogTabs>

            <div v-if="activeTab === 'removals' && selectedStoreId" class="removal-metrics-grid">
              <div class="removal-metric-card removal-metric-card--blue">
                <div class="removal-metric-card__icon">
                  <vue-feather type="archive" size="19" />
                </div>
                <div>
                  <p class="removal-metric-card__label">Filtered Removals</p>
                  <p class="removal-metric-card__value">{{ totalCatalog }}</p>
                </div>
              </div>

              <div class="removal-metric-card removal-metric-card--amber">
                <div class="removal-metric-card__icon">
                  <vue-feather type="minus-circle" size="19" />
                </div>
                <div>
                  <p class="removal-metric-card__label">Quantity Removed</p>
                  <p class="removal-metric-card__value">{{ formattedRemovalTotalQuantity }}</p>
                </div>
              </div>

              <div class="removal-metric-card removal-metric-card--green">
                <div class="removal-metric-card__icon">
                  <vue-feather type="credit-card" size="19" />
                </div>
                <div>
                  <p class="removal-metric-card__label">Total Amount</p>
                  <p class="removal-metric-card__value">{{ formattedRemovalTotalAmount }}</p>
                </div>
              </div>
            </div>

            <!-- Unified table across tabs -->
            <div v-if="activeTab === 'packaging'" class="catalog-packaging-shell">
              <div class="catalog-packaging-header">
                <div>
                  <p class="catalog-packaging-title">Quantity Packaging</p>
                  <p class="catalog-packaging-description">
                    Manage store quantity packagings that also appear in the product Quantity Unit dropdown.
                  </p>
                </div>
                <div class="catalog-packaging-actions">
                  <hs-button variant="secondary" icon="refresh-cw" :loading="loadingQuantityUnits"
                    @click="getQuantityUnits">
                    Refresh
                  </hs-button>
                  <hs-button icon="plus" @click="openPackagingModal()">
                    Add Packaging
                  </hs-button>
                </div>
              </div>

              <HSTable class="catalog-packaging-table"
                :headers="['Packaging', 'Base Unit', 'Unit Count', 'Description', 'Actions']"
                :data="visibleQuantityUnits" :headerKeyMapping="{
                  Packaging: 'name',
                  'Base Unit': 'baseUnit',
                  'Unit Count': 'factorLabel',
                  Description: 'description',
                  Actions: 'actions',
                }">
                <template #cell-packaging="{ row }">
                  <div class="catalog-packaging-name">
                    <span>{{ row.name }}</span>
                    <small>{{ row.unit }}</small>
                  </div>
                </template>
                <template #cell-base_unit="{ value }">
                  <span class="catalog-packaging-pill">{{ value }}</span>
                </template>
                <template #cell-unit_count="{ value }">
                  <span class="catalog-packaging-factor">{{ value }}</span>
                </template>
                <template #cell-description="{ value }">
                  <span>{{ value || 'No description' }}</span>
                </template>
                <template #cell-actions="{ row }">
                  <div class="catalog-packaging-row-actions">
                    <hs-tooltip text="Edit packaging">
                      <hs-button variant="secondary" icon="edit-2" icon-only @click.stop="openPackagingModal(row)" />
                    </hs-tooltip>
                    <hs-tooltip text="Delete packaging">
                      <hs-button variant="danger" icon="trash-2" icon-only @click.stop="confirmDeletePackaging(row)" />
                    </hs-tooltip>
                  </div>
                </template>
                <template #empty>
                  <div class="catalog-packaging-empty">
                    <vue-feather type="layers" size="24" />
                    <div>
                      <p>No custom quantity packaging yet</p>
                      <span>Add packagings like carton, crate, or sack so products can use them as quantity
                        units.</span>
                    </div>
                    <hs-button icon="plus" @click="openPackagingModal()">
                      Add Packaging
                    </hs-button>
                  </div>
                </template>
              </HSTable>
            </div>
            <CatalogTable v-else :catalog="activeTab === 'products'
              ? products
              : activeTab === 'services'
                ? services
                : activeTab === 'purchases'
                  ? purchases
                  : activeTab === 'transfers'
                    ? transfers
                    : activeTab === 'transfer_requests'
                      ? transferRequests
                      : activeTab === 'removals'
                        ? removals
                        : inventory
              " :selected-catalog="selectedCatalog" @select-all="handleSelectAll" @select-item="handleSelectItem"
              @edit-item="handleEditItem" @delete-item="handleDeleteItem" @save-to-ebm="handleSaveToEbm"
              @edit-ebm-data="handleEditEbmData" @view-transfer="handleViewTransfer"
              @view-request="handleViewTransferRequest" @start-request="handleStartTransferRequest"
              @complete-request="handleCompleteTransferRequest" @reject-request="rejectTransferRequest"
              @open-mappings="openProductMappings" @row-click="handleCatalogRowClick"
              @view-item-history="handleViewItemHistory" @previous-page="handlePreviousPage" @next-page="handleNextPage"
              :current-page="currentPage" :catalog-per-page="catalogPerPage" :total-catalog="totalCatalog"
              :total-pages="totalPages" :loading="tabLoading" @update:catalog-per-page="handlePerPageChange"
              :active-tab="activeTab" :loading-ebm-activation="loadingEbmActivation" :store-id="selectedStoreId"
              :show-inventory-commitments="inventoryCommitmentsEnabled"
              :price-analytics="priceAnalytics" :loading-price-analytics="loadingPriceAnalytics"
              :open-price-analytics-product-id="openPriceAnalyticsProductId" @price-updated="handlePurchasePriceUpdated"
              @open-price-modal="handleOpenPriceModal" @toggle-price-analytics="handleTogglePriceAnalytics"
              :ebm-activated="ebmSettings?.ebm_activated || false" />
          </div>

          <!-- Edit Modal -->
          <EditCatalogModal :activeTab="activeTab" :is-open="showEditModal" :item="selectedItem"
            :store-id="selectedStoreId" :is-creating="isCreatingItem" @close="handleCloseModal" @save="handleSaveItem"
            :loading-modify-product="loadingAddProduct || loadingEditProduct || loadingDeleteProduct
              " :loading-modify-service="loadingAddService || loadingEditService || loadingDeleteService
                " />

          <!-- Item History Modal -->
          <ItemHistoryModal :is-open="showItemHistoryModal" :item-id="itemHistoryId" :item-type="itemHistoryType"
            :item-name="itemHistoryName" @close="showItemHistoryModal = false" />
          <InventoryReconciliationModal v-if="inventoryCommitmentsEnabled" v-model="showInventoryReconciliation" :store-id="selectedStoreId"
            @reconciled="handleInventoryReconciled" />
          <InventoryPolicyModal v-model="showInventoryPolicy" :store-id="selectedStoreId"
            :settings="inventorySettings" @saved="handleInventoryPolicySaved" />
        </div>

        <!-- Sync Modal -->
        <StoreItemSync v-if="showSyncModal" :store-id="selectedStoreId" @close="showSyncModal = false"
          @sync-completed="handleSyncCompleted" />
      </div>
    </div>

    <hs-modal v-model="showBulkActions" title="Bulk Actions" kicker="Catalog tools"
      description="Upload catalog items or download the right template for your next import." size="lg">
      <div class="catalog-bulk-actions">
        <hs-button variant="secondary" class="bulk-action-card bulk-action-card--primary"
          @click="showSyncModal = true; showBulkActions = false">
          <div class="bulk-action-card__icon">
            <vue-feather type="upload" size="20" />
          </div>
          <div>
            <h4>Upload Catalog Items</h4>
            <p>Sync products, services, and inventory using your prepared catalog file.</p>
          </div>
          <vue-feather type="arrow-right" size="18" />
        </hs-button>

        <div class="bulk-action-card bulk-action-card--template">
          <div class="bulk-action-card__icon">
            <vue-feather type="file-text" size="20" />
          </div>
          <div class="bulk-action-card__content">
            <h4>Catalog Templates</h4>
            <p>Choose a template type and download a clean import file.</p>
            <div class="bulk-template-row">
              <hs-select v-model="selectedTemplate" :options="templateOptions" />
              <hs-button @click="downloadSelectedTemplate" variant="success"
                :icon="getDownloadButtonIcon(selectedTemplate)" :disabled="!selectedTemplate"
                :title="getDownloadButtonTooltip(selectedTemplate)">
                Download
              </hs-button>
            </div>
          </div>
        </div>
      </div>
    </hs-modal>

    <!-- Delete Confirmation Modal -->
    <confirmation-modal :is-open="showConfirmationModal" :title="confirmationTitle" :message="confirmationMessage"
      :confirm-text="confirmationConfirmText" :cancel-text="confirmationCancelText" @confirm="handleConfirmDelete"
      @close="showConfirmationModal = false" :loading="loadingDeleteProduct || loadingDeleteService" />

    <PriceUpdateModal v-if="priceModalType" :is-open="showPriceModal" :price-type="priceModalType"
      :item-id="priceModalItemId" :current-price="priceModalCurrentPrice" :currency="priceModalCurrency"
      :item-name="priceModalItemName" :item="priceModalItem" @close="handleClosePriceModal"
      @price-updated="handlePriceModalUpdated" />

    <!-- Copy Selected Items to Stores -->
    <CopyCatalogToStores v-if="showCopyModal" :selected-items="selectedCatalog" @close-modal="handleCopyModalClose" />

    <!-- Save to EBM Modal -->
    <SaveToEbmModal v-if="selectedEbmItem" :is-open="showEbmModal" :item="selectedEbmItem" :active-tab="activeTab"
      :ebm-settings="ebmSettings" :loading="loadingEbmSettings" @close="handleCloseEbmModal"
      @confirm="handleConfirmSaveToEbm" />

    <!-- EBM Data Editor Modal -->
    <EbmDataEditorModal v-if="selectedInventoryItem" :is-open="showEbmDataEditor"
      :inventory-item="selectedInventoryItem" :loading="loadingEbmDataEditor" @close="handleCloseEbmDataEditor"
      @saved="handleEbmDataSaved" @success="handleEbmDataSuccess" @error="handleEbmDataError"
      @validation-error="handleEbmValidationError" @loading="loadingEbmDataEditor = $event" />

    <TransferRequestDetailsModal v-if="showRequestDetails" :is-open="showRequestDetails" :group-id="selectedGroupId"
      :request="selectedRequestRaw" :store-name="storeName" @close="
        showRequestDetails = false;
      selectedRequestRaw = null;
      " @start="handleStartTransferRequest($event)" @complete="handleCompleteTransferRequest($event)"
      @reject="rejectTransferRequest($event)" />

    <TransferRequestModal v-if="showRequestModal" :is-open="showRequestModal" :target-store-id="selectedStoreId"
      :selected-items="selectedCatalog" @close="showRequestModal = false" @submitted="handleRequestSubmitted" />

    <TransferDetailsModal v-if="showTransferDetails" :is-open="showTransferDetails" :group-id="selectedGroupId"
      :transfer="selectedTransferRaw" :store-name="storeName" @close="
        showTransferDetails = false;
      selectedTransferRaw = null;
      " />

    <hs-modal v-model="showPackagingModal"
      :title="editingPackaging?.id ? 'Edit Quantity Packaging' : 'Add Quantity Packaging'"
      description="Create store-specific packagings that become available in product Quantity Unit selections."
      size="lg">
      <form class="catalog-packaging-form" @submit.prevent="savePackaging">
        <hs-input v-model="packagingForm.label" label="Packaging Name" icon="tag" placeholder="Carton, crate, sack..."
          required />
        <div class="catalog-packaging-form-grid">
          <hs-select v-model="packagingForm.baseUnit" :options="baseQuantityUnitOptions" label="Base Unit" icon="box" />
          <hs-input v-model="packagingForm.factor" type="number" label="Unit Count" icon="hash"
            placeholder="How many base units?" required />
        </div>
        <hs-textarea v-model="packagingForm.description" label="Description"
          placeholder="Optional note for this packaging" rows="3" />
        <div class="catalog-packaging-form-actions">
          <hs-button type="button" variant="secondary" @click="showPackagingModal = false">
            Cancel
          </hs-button>
          <hs-button type="submit" icon="check" :loading="savingPackaging" :disabled="savingPackaging">
            Save Packaging
          </hs-button>
        </div>
      </form>
    </hs-modal>

    <hs-confirm-dialog v-model="showDeletePackagingConfirm" title="Delete Quantity Packaging"
      :message="`Delete ${packagingToDelete?.name || 'this packaging'}? Products using it may need a new quantity unit.`"
      confirm-text="Delete" tone="danger" :loading="deletingPackaging" @confirm="deletePackaging" />
  </div>
</template>

<script>
import StoreItemSync from "@/components/modals/store/StoreItemSync.vue";
import CatalogTable from "@/components/catalog/CatalogTable.vue";
import EditCatalogModal from "@/components/catalog/EditCatalogModal.vue";
import CatalogTabs from "@/components/catalog/CatalogTabs.vue";
import CopyCatalogToStores from "@/components/catalog/CopyCatalogToStores.vue";
import PriceUpdateModal from "@/components/catalog/PriceUpdateModal.vue";
import SaveToEbmModal from "@/components/modals/ebm/SaveToEbmModal.vue";
import EbmDataEditorModal from "@/components/modals/ebm/EbmDataEditorModal.vue";
import TransferRequestDetailsModal from "@/components/catalog/TransferRequestDetailsModal.vue";
import TransferRequestModal from "@/components/catalog/TransferRequestModal.vue";
import TransferDetailsModal from "@/components/catalog/TransferDetailsModal.vue";
import ItemHistoryModal from "@/components/catalog/ItemHistoryModal.vue";
import InventoryReconciliationModal from "@/components/catalog/InventoryReconciliationModal.vue";
import InventoryPolicyModal from "@/components/catalog/InventoryPolicyModal.vue";
import HSCustomSelect from "@/components/common/HSCustomSelect.vue";
import HSTable from "@/components/table/HSTable.vue";
import {
  HsButton,
  HsConfirmDialog,
  HsDateInput,
  HsInput,
  HsModal,
  HsSelect,
  HsTextarea,
  HsTooltip,
} from "@/components/ui";
import http from "@/helper/http";
import api from "@/helper/api";
import { formatDistanceToNowStrict } from "date-fns";
import { toast } from "vue3-toastify";
import { mapActions, mapGetters } from "vuex";
import EbmDataService from "@/services/ebmDataService";
import { useInvalidateTransfers } from "@/composables/useTransfersQuery";
import { useInvalidateMappings } from "@/composables/useProductMappingsQuery";

export default {
  name: "StoreCatalogView",
  setup() {
    const { invalidateTransfers, invalidateRequests, invalidateRemovals } =
      useInvalidateTransfers();
    const { invalidateMappings } = useInvalidateMappings();
    return {
      invalidateTransfers,
      invalidateRequests,
      invalidateRemovals,
      invalidateMappings,
    };
  },
  components: {
    StoreItemSync,
    CatalogTable,
    EditCatalogModal,
    CatalogTabs,
    HSTable,
    CopyCatalogToStores,
    PriceUpdateModal,
    SaveToEbmModal,
    EbmDataEditorModal,
    TransferRequestDetailsModal,
    TransferRequestModal,
    TransferDetailsModal,
    ItemHistoryModal,
    InventoryReconciliationModal,
    InventoryPolicyModal,
    HSCustomSelect,
    HsButton,
    HsConfirmDialog,
    HsDateInput,
    HsInput,
    HsModal,
    HsSelect,
    HsTextarea,
    HsTooltip,
  },
  data() {
    return {
      showBulkActions: false,
      showSyncModal: false,
      selectedTemplate: "products",
      templateConfig: {
        products: {
          label: "Products Template",
          filename: "HovaStore Products Template.xlsx",
          icon: "package",
          color: "bg-blue-600 hover:bg-blue-700",
          description: "Download template for product catalog items",
        },
        services: {
          label: "Services Template",
          filename: "HovaStore Services Template.xlsx",
          icon: "settings",
          color: "bg-purple-600 hover:bg-purple-700",
          description: "Download template for service catalog items",
        },
      },
      // Catalog state
      showEditModal: false,
      activeTab: "inventory",
      selectedCatalog: [],
      selectedItem: null,
      products: [],
      services: [],
      inventory: [],
      purchases: [],
      transfers: [],
      transferRequests: [],
      removals: [],
      quantityUnits: [],
      loadingQuantityUnits: false,
      showPackagingModal: false,
      savingPackaging: false,
      deletingPackaging: false,
      editingPackaging: null,
      packagingToDelete: null,
      showDeletePackagingConfirm: false,
      packagingForm: {
        label: "",
        factor: "",
        baseUnit: "U",
        description: "",
      },
      baseQuantityUnitOptions: [
        { value: "U", label: "Pieces / items" },
        { value: "KG", label: "Kilograms" },
        { value: "L", label: "Liters" },
        { value: "M", label: "Meters" },
      ],
      // Request details modal
      showRequestDetails: false,
      selectedRequestRaw: null,
      // Transfer details modal
      showTransferDetails: false,
      selectedTransferRaw: null,
      // Item history modal
      showItemHistoryModal: false,
      itemHistoryId: null,
      itemHistoryType: "product",
      itemHistoryName: "",
      showInventoryReconciliation: false,
      showInventoryPolicy: false,
      inventorySettings: null,
      inventoryCommitmentsEnabled: false,
      openDeficitCount: 0,
      currentPage: 1,
      catalogPerPage: 10,
      totalCatalog: 0,
      totalPages: 0,
      isCreatingItem: false,
      sortBy: "name", // Default sort by name
      expirationFilter: "all", // Default expiration filter (all, hide_expired, expired_only)
      priceAnalytics: {}, // Store price analytics data: { productId: { min, max, remaining } }
      loadingPriceAnalytics: false,
      openPriceAnalyticsProductId: null, // Track which product has tooltip open
      showPriceModal: false,
      priceModalType: null,
      priceModalItemId: null,
      priceModalItemName: "",
      priceModalCurrentPrice: 0,
      priceModalCurrency: "RWF",
      priceModalItem: null,
      showConfirmationModal: false,
      confirmationTitle: "",
      confirmationMessage: "",
      confirmationConfirmText: "",
      confirmationCancelText: "",
      showCopyModal: false,
      showRequestModal: false,
      hasInitialCatalogLoaded: false,
      stats: {
        totalItems: 0,
        products: 0,
        services: 0,
        totalStockCost: 0,
        totalStockSellingValue: 0,
        stockValuationStale: false,
        stockValuationCachedAt: null,
      },
      // EBM related state
      showEbmModal: false,
      selectedEbmItem: null,
      loadingEbmSettings: false,
      loadingEbmActivation: false,
      // New EBM data editor state
      showEbmDataEditor: false,
      selectedInventoryItem: null,
      loadingEbmDataEditor: false,
      // Search state
      searchQuery: "",
      // Loading states
      loadingInventory: false,
      loadingPurchases: false,
      isUpdatingRoute: false,
      // Group context for transfers/requests
      storeGroups: [],
      selectedGroupId: "",
      groupStoresForFilter: [],
      transferRequestStoreFilter: "",
      transferStoreFilter: "",
      removalReasons: [],
      loadingRemovalReasons: false,
      downloadingRemovalReport: false,
      removalFilters: {
        reasonId: "",
        startDate: "",
        endDate: "",
      },
    };
  },
  computed: {
    // Dynamic template list from configuration
    availableTemplates() {
      return Object.keys(this.templateConfig).map((key) => ({
        key,
        ...this.templateConfig[key],
      }));
    },
    templateOptions() {
      return this.availableTemplates.map((template) => ({
        label: template.label,
        value: template.key,
      }));
    },
    stockValuationTooltip() {
      if (!this.stats.stockValuationStale) {
        return "";
      }

      const relativeTimestamp = this.formatRelativeTimestamp(
        this.stats.stockValuationCachedAt,
      );

      if (!relativeTimestamp) {
        return "Data is stale. Refresh time unavailable.";
      }

      return `Data is stale. Last refreshed ${relativeTimestamp}.`;
    },

    // Get current template configuration
    currentTemplate() {
      return (
        this.templateConfig[this.selectedTemplate] ||
        this.templateConfig.products
      );
    },
    // Modules are not namespaced; use root getters
    ...mapGetters(["getUserStores", "isLoadingStores"]),
    // Loading states from products/services modules
    ...mapGetters([
      "loadingProducts",
      "loadingAddProduct",
      "loadingEditProduct",
      "loadingDeleteProduct",
      "loadingServices",
      "loadingAddService",
      "loadingEditService",
      "loadingDeleteService",
    ]),
    // Transfers module
    ...mapGetters("transfers", {
      transfersFromStore: "transfers",
      transferRequestsFromStore: "transferRequests",
      removalsFromStore: "removals",
      removalsSummaryFromStore: "removalsSummary",
      transfersLoading: "loadingTransfers",
      transferRequestsLoading: "loadingTransferRequests",
      removalsLoading: "loadingRemovals",
      transfersPagination: "transfersPagination",
      requestsPagination: "requestsPagination",
      removalsPagination: "removalsPagination",
    }),
    // Inventory module
    ...mapGetters("inventory", {
      inventoryFromStore: "inventory",
      inventoryLoading: "loadingInventory",
      inventoryPagination: "inventoryPagination",
    }),
    // Products module
    ...mapGetters("catalogProducts", {
      productsFromStore: "products",
      servicesFromStore: "services",
      productsLoading: "loadingProducts",
      servicesLoading: "loadingServices",
      productsPagination: "productsPagination",
      servicesPagination: "servicesPagination",
    }),
    // Purchases module
    ...mapGetters("catalogPurchases", {
      purchasesFromStore: "purchases",
      purchasesLoading: "loadingPurchases",
      purchasesPagination: "purchasesPagination",
    }),
    // Groups module
    ...mapGetters("catalogGroups", {
      storeGroupsFromStore: "storeGroups",
      groupStoresFromStore: "groupStores",
      loadingStoreGroups: "loadingStoreGroups",
      loadingGroupStores: "loadingGroupStores",
    }),
    stores() {
      return this.getUserStores || [];
    },
    selectedStoreId() {
      return this.$store.state.dashboard?.selectedStore;
    },
    selectedStore() {
      return this.stores.find(
        (store) => String(store.id) === String(this.selectedStoreId),
      );
    },
    groupSelectOptions() {
      return [
        { value: "", label: "Select Group" },
        ...this.storeGroups.map((group) => ({
          value: group.id,
          label: group.name,
        })),
      ];
    },
    currentStoreFilterLabel() {
      return this.selectedStoreId
        ? `${this.storeName(this.selectedStoreId)} (Current Store)`
        : "Current Store (Default)";
    },
    transferStoreSelectOptions() {
      return [
        { value: "", label: this.currentStoreFilterLabel },
        { value: "all", label: "All Stores" },
        ...this.groupStoresForFilter.map((store) => ({
          value: store.id,
          label: store.name,
        })),
      ];
    },
    visibleQuantityUnits() {
      const query = String(this.searchQuery || "").trim().toLowerCase();
      if (!query) {
        return this.quantityUnits;
      }
      return this.quantityUnits.filter((unit) =>
        [
          unit.name,
          unit.label,
          unit.unit,
          unit.baseUnit,
          unit.description,
          unit.factorLabel,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query)),
      );
    },
    removalReasonSelectOptions() {
      return [
        { value: "", label: "All Reasons" },
        ...this.removalReasons.map((reason) => ({
          value: String(reason.id),
          label: reason.name,
        })),
      ];
    },
    removalSummary() {
      return (
        this.removalsSummaryFromStore || {
          total_amount: 0,
          total_quantity_removed: 0,
          currency_code: "RWF",
        }
      );
    },
    formattedRemovalTotalQuantity() {
      return Number(
        this.removalSummary?.total_quantity_removed || 0,
      ).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formattedRemovalTotalAmount() {
      const currencyCode =
        this.removalSummary?.currency_code ||
        this.selectedStore?.store_currency_code ||
        "RWF";
      const amount = Number(this.removalSummary?.total_amount || 0);
      return `${currencyCode} ${amount.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
    },
    ebmSettings() {
      return this.$store.getters.rraEbmSettings;
    },
    tabLoading() {
      if (this.activeTab === "inventory") {
        return this.inventoryLoading || this.loadingEbmActivation;
      }
      if (this.activeTab === "products") {
        return this.productsLoading;
      }
      if (this.activeTab === "services") {
        return this.servicesLoading;
      }
      if (this.activeTab === "purchases") {
        return this.purchasesLoading;
      }
      if (this.activeTab === "transfers") {
        return this.transfersLoading;
      }
      if (this.activeTab === "transfer_requests") {
        return this.transferRequestsLoading;
      }
      if (this.activeTab === "removals") {
        return this.removalsLoading;
      }
      if (this.activeTab === "packaging") {
        return this.loadingQuantityUnits;
      }
      return false;
    },
  },
  watch: {
    stores: {
      handler(newStores) {
        this.applyInitialStoreSelection(newStores);
      },
      immediate: true,
    },
    $route: {
      handler(newRoute) {
        this.handleRouteChange(newRoute);
      },
      immediate: true,
    },
    async selectedStoreId(newStoreId, oldStoreId) {
      if (String(newStoreId) === String(oldStoreId)) {
        return;
      }

      if (!newStoreId) {
        // Clear other components that rely on store data
        this.resetStats();
        this.selectedCatalog = [];
        this.openDeficitCount = 0;
        this.inventoryCommitmentsEnabled = false;
        this.showInventoryReconciliation = false;
        this.loadingEbmActivation = false;
        return;
      }

      const hasStore = this.stores.some(
        (store) => String(store.id) === String(newStoreId),
      );

      if (!hasStore) {
        this.resetStats();
        this.openDeficitCount = 0;
        this.inventoryCommitmentsEnabled = false;
        this.showInventoryReconciliation = false;
        this.loadingEbmActivation = false;
        return;
      }

      // Step 1: Clear other components that rely on store data and add loader
      this.resetStats();
      this.selectedCatalog = [];
      this.inventoryCommitmentsEnabled = false;
      this.openDeficitCount = 0;
      this.showInventoryReconciliation = false;
      this.loadingEbmActivation = true;
      this.currentPage = 1;
      this.removalReasons = [];
      this.resetRemovalFilters();

      this.$store.commit("SET_SELECTED_STORE", newStoreId);

      try {
        await this.loadInventoryCommitmentSettings(newStoreId);
        // Step 2: EBM settings are fetched by DashboardLayout when store changes
        // Step 3: Fetch the items on the list
        this.handleDataChange(this.searchQuery);
        // Refresh groups for transfers contexts
        this.fetchStoreGroups();
        // Keep the URL in sync with the current tab so refresh preserves it
        await this.updateUrlParams();

        // Ensure initial fetches also run after a short tick to avoid race with UI
        this.$nextTick(() => {
          this.fetchCatalogStats();
        });
      } catch (error) {
        console.error("Error during store change sequence:", error);
      } finally {
        // Set loading to false after a short delay to allow EBM settings to load
        setTimeout(() => {
          this.loadingEbmActivation = false;
        }, 200);
      }
    },
  },
  methods: {
    isStockValuationUnavailable(value) {
      return (
        this.stats.stockValuationStale ||
        this.stats.stockValuationCachedAt == null ||
        value === null ||
        value === undefined
      );
    },
    formatStockValuationAmount(value) {
      if (this.isStockValuationUnavailable(value)) {
        return "--";
      }

      return this.formatStatsAmount(value);
    },
    // Modules are not namespaced; use root actions
    ...mapActions(["getAllUsersStore"]),
    // Transfers module actions
    ...mapActions("transfers", {
      fetchTransfersAction: "fetchTransfers",
      fetchTransferRequestsAction: "fetchTransferRequests",
      fetchRemovalsAction: "fetchRemovals",
      updateRequestStatusAction: "updateTransferRequestStatus",
    }),
    // Inventory module actions
    ...mapActions("inventory", {
      fetchInventoryAction: "fetchInventory",
    }),
    // Products module actions
    ...mapActions("catalogProducts", {
      fetchProductsAction: "fetchProducts",
      fetchServicesAction: "fetchServices",
    }),
    // Purchases module actions
    ...mapActions("catalogPurchases", {
      fetchPurchasesAction: "fetchPurchases",
    }),
    // Groups module actions
    ...mapActions("catalogGroups", {
      fetchStoreGroupsAction: "fetchStoreGroups",
      fetchGroupStoresAction: "fetchGroupStores",
    }),
    async loadStores() {
      try {
        const response = await this.getAllUsersStore();
        this.applyInitialStoreSelection(response?.stores ?? []);
      } catch (error) {
        toast.error("Failed to load stores");
        console.error("Load stores error:", error);
      }
    },
    // Dynamic methods for template handling
    getTemplateFilename(type) {
      const template = this.templateConfig[type];
      return template ? template.filename : "HovaStore Template.xlsx";
    },

    getDownloadButtonClass(templateKey) {
      const template = this.templateConfig[templateKey];
      return template ? template.color : "bg-gray-600 hover:bg-gray-700";
    },

    getDownloadButtonIcon(templateKey) {
      const template = this.templateConfig[templateKey];
      return template ? template.icon : "download";
    },

    getDownloadButtonTooltip(templateKey) {
      const template = this.templateConfig[templateKey];
      return template ? template.description : "Download template";
    },

    // Download selected template from dropdown
    async downloadSelectedTemplate() {
      if (!this.selectedTemplate) {
        toast.error("Please select a template type");
        return;
      }
      await this.downloadTemplate(this.selectedTemplate);
    },

    // Enhanced download method with dynamic configuration
    async downloadTemplate(type) {
      try {
        const template = this.templateConfig[type];
        if (!template) {
          toast.error("Template configuration not found");
          return;
        }

        const a = document.createElement("a");
        a.href = `/${encodeURIComponent(template.filename)}`;
        a.download = template.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        toast.success(`${template.label} download started`);
      } catch (error) {
        toast.error("Failed to trigger template download");
        console.error("Template link error:", error);
      }
    },

    // Utility method to add new templates dynamically
    addTemplate(key, config) {
      this.$set(this.templateConfig, key, config);
    },

    // Utility method to remove templates
    removeTemplate(key) {
      this.$delete(this.templateConfig, key);
      if (this.selectedTemplate === key) {
        this.selectedTemplate = Object.keys(this.templateConfig)[0];
      }
    },
    handleSyncCompleted() {
      // Keep modal open so the user can review results
      // Refresh catalog list and stats
      this.handleDataChange(this.searchQuery);
      this.fetchCatalogStats();
    },
    updateStats(newStats) {
      this.stats = { ...this.stats, ...newStats };
    },
    async fetchCatalogStats() {
      if (!this.selectedStoreId) return;
      try {
        const { data } = await http.get(
          `${api.store_catalog_stats}/${this.selectedStoreId}`,
        );
        this.stats = {
          totalItems: Number(data?.total_items || 0),
          products: Number(data?.total_products || 0),
          services: Number(data?.total_services || 0),
          totalStockCost: Number(data?.total_stock_cost || 0),
          totalStockSellingValue: Number(data?.total_stock_selling_value || 0),
          stockValuationStale: data?.stock_valuation_stale === true,
          stockValuationCachedAt: data?.stock_valuation_cached_at || null,
        };
      } catch (err) {
        // Silent fail; stats are non-critical for catalog operations
        console.error("Failed to load catalog stats", err);
      }
    },
    resetStats() {
      this.stats = {
        totalItems: 0,
        products: 0,
        services: 0,
        totalStockCost: 0,
        totalStockSellingValue: 0,
        stockValuationStale: false,
        stockValuationCachedAt: null,
      };
    },
    formatStatsAmount(value) {
      const parsedValue = Number(value || 0);
      return parsedValue.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    numericValue(value, fallback = 0) {
      if (value === null || value === undefined || value === "") {
        return fallback;
      }
      const parsedValue = Number(value);
      return Number.isFinite(parsedValue) ? parsedValue : fallback;
    },
    mapInventoryCatalogItem(p = {}) {
      const product = p?.product || {};
      const unit = product.default_quantity_unit || product.quantity_unit || "U";
      const onHandQuantity = this.numericValue(
        p?.on_hand_quantity ?? p?.quantity,
      );
      const reservedQuantity = this.numericValue(p?.reserved_quantity);
      const deficitQuantity = this.numericValue(p?.deficit_quantity);
      const availableQuantity = this.numericValue(
        p?.available_quantity,
        onHandQuantity - reservedQuantity - deficitQuantity,
      );
      const sellableQuantity = this.numericValue(
        p?.sellable_quantity,
        Math.max(availableQuantity, 0),
      );

      return {
        id: product.id,
        name: product.name,
        sku: product.item_sku || "",
        barcode: product.barcode || "",
        quantity: `${this.formatQuantity(onHandQuantity)} ${unit}`,
        priceFormatted: this.formatPrice(p?.price),
        expiration_date: p?.expiration_date,
        has_rra_ebm_metadata: product.has_rra_ebm_metadata || false,
        product,
        price: p?.price,
        salePrices: p?.sale_prices || [],
        sale_prices: p?.sale_prices || [],
        quantity_unit: product.quantity_unit || unit,
        default_quantity_unit: unit,
        quantityRaw: p?.quantity,
        onHandQuantity,
        reservedQuantity,
        availableQuantity,
        deficitQuantity,
        sellableQuantity,
        inventoryState: p?.inventory_state || (
          deficitQuantity > 0
            ? "DEFICIT"
            : availableQuantity <= 0
              ? "UNAVAILABLE"
              : "AVAILABLE"
        ),
        originalData: p,
      };
    },
    formatRelativeTimestamp(timestamp) {
      if (!timestamp) {
        return null;
      }

      const parsedDate = new Date(timestamp);
      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }

      return formatDistanceToNowStrict(parsedDate, { addSuffix: true });
    },
    async applyInitialStoreSelection(storesList = []) {
      const availableStores = Array.isArray(storesList) ? storesList : [];

      if (availableStores.length === 0) {
        this.$store.commit("SET_SELECTED_STORE", null);
        this.resetStats();
        return;
      }

      const currentStoreId = this.$store.state.dashboard?.selectedStore;
      const currentSelectionStillValid = availableStores.some(
        (store) => String(store.id) === String(currentStoreId),
      );

      if (currentSelectionStillValid) {
        // On refresh, selection might already be set and unchanged; ensure we fetch once
        if (!this.hasInitialCatalogLoaded && currentStoreId) {
          this.hasInitialCatalogLoaded = true;
          await this.loadInventoryCommitmentSettings(currentStoreId);
          this.fetchCatalogStats();
          this.handleDataChange(this.searchQuery);
          // Push current tab/sort/search into the URL so reloads preserve state
          this.updateUrlParams();
        }
        return;
      }

      const nextStoreId = this.getInitialStoreId(availableStores);
      this.$store.commit("SET_SELECTED_STORE", nextStoreId);
      // Defer initial fetch to the watcher, but set flag after it runs
      this.$nextTick(() => {
        this.hasInitialCatalogLoaded = false;
      });
    },
    getInitialStoreId(storesList = []) {
      // First, check if there's a store ID in the route params
      const routeStoreId = this.$route.params.storeId;
      if (routeStoreId) {
        const routeMatch = storesList.find(
          (store) => String(store.id) === String(routeStoreId),
        );
        if (routeMatch) {
          return parseInt(routeStoreId);
        }
      }

      // Then check persisted selection
      const persistedSelection = this.$store.state.dashboard?.selectedStore;
      if (persistedSelection) {
        const persistedMatch = storesList.find(
          (store) => String(store.id) === String(persistedSelection),
        );

        if (persistedMatch) {
          return persistedMatch.id;
        }
      }

      return storesList[0]?.id ?? null;
    },
    // Catalog handlers
    handleDataChange(searchQuery) {
      if (!this.selectedStoreId) return;
      const effectiveQuery =
        typeof searchQuery === "string" ? searchQuery : this.searchQuery || "";

      if (this.activeTab === "products") {
        return this.getProducts(effectiveQuery);
      } else if (this.activeTab === "services") {
        return this.getServices(effectiveQuery);
      } else if (this.activeTab === "purchases") {
        return this.getPurchases(effectiveQuery);
      } else if (this.activeTab === "transfers") {
        return this.getTransfers();
      } else if (this.activeTab === "transfer_requests") {
        return this.getTransferRequests();
      } else if (this.activeTab === "removals") {
        return this.getRemovals();
      } else if (this.activeTab === "packaging") {
        return this.getQuantityUnits();
      } else {
        return this.getInventory(effectiveQuery);
      }
    },
    async handleSortChange(sortBy) {
      this.sortBy = sortBy;
      this.currentPage = 1; // Reset to first page when sorting changes
      await this.handleDataChange(this.searchQuery);
      await this.updateUrlParams();
    },
    async handleExpirationFilterChange(filterValue) {
      this.expirationFilter = filterValue;
      this.currentPage = 1; // Reset to first page when filter changes
      await this.handleDataChange(this.searchQuery);
      await this.updateUrlParams();
    },
    resetRemovalFilters() {
      this.removalFilters = {
        reasonId: "",
        startDate: "",
        endDate: "",
      };
    },
    buildRemovalDateBoundary(dateValue, endOfDay = false) {
      if (!dateValue) {
        return null;
      }

      const timeSuffix = endOfDay ? "T23:59:59.999" : "T00:00:00.000";
      const parsedDate = new Date(`${dateValue}${timeSuffix}`);

      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }

      return parsedDate.toISOString();
    },
    buildRemovalFilters() {
      const filters = {};

      const normalizedSearchQuery =
        typeof this.searchQuery === "string" ? this.searchQuery.trim() : "";

      if (normalizedSearchQuery) {
        filters.query = normalizedSearchQuery;
      }

      if (this.removalFilters.reasonId) {
        filters["removal_reason_id[eq]"] = this.removalFilters.reasonId;
      }

      const startDate = this.buildRemovalDateBoundary(
        this.removalFilters.startDate,
      );
      if (startDate) {
        filters["created_at[gte]"] = startDate;
      }

      const endDate = this.buildRemovalDateBoundary(
        this.removalFilters.endDate,
        true,
      );
      if (endDate) {
        filters["created_at[lte]"] = endDate;
      }

      return filters;
    },
    mergeRemovalReasons(items = []) {
      const removalReasonsById = new Map(
        (this.removalReasons || [])
          .filter((reason) => reason?.id != null)
          .map((reason) => [String(reason.id), reason]),
      );

      items.forEach((item) => {
        const reason = item?.removal_reason;
        if (!reason || reason.id == null) {
          return;
        }

        removalReasonsById.set(String(reason.id), {
          id: reason.id,
          name: reason.name,
          description: reason.description,
        });
      });

      this.removalReasons = Array.from(removalReasonsById.values()).sort(
        (left, right) =>
          String(left?.name || "").localeCompare(String(right?.name || "")),
      );
    },
    async ensureRemovalReasonsLoaded(forceRefresh = false) {
      if (!this.selectedStoreId || this.loadingRemovalReasons) {
        return;
      }

      if (!forceRefresh && this.removalReasons.length > 0) {
        return;
      }

      this.loadingRemovalReasons = true;
      try {
        const response = await http.get(
          `/store/inventory/${this.selectedStoreId}/removal/reasons`,
        );
        this.removalReasons = Array.isArray(response.data)
          ? [...response.data].sort((left, right) =>
            String(left?.name || "").localeCompare(String(right?.name || "")),
          )
          : [];
      } catch (error) {
        console.error("Failed to load removal reasons", error);
        this.removalReasons = [];
      } finally {
        this.loadingRemovalReasons = false;
      }
    },
    async getQuantityUnits() {
      if (!this.selectedStoreId) {
        this.quantityUnits = [];
        return;
      }
      if (this.loadingQuantityUnits) {
        return;
      }
      this.loadingQuantityUnits = true;
      try {
        const response = await http.get(this.customQuantityUnitsEndpoint());
        const units = Array.isArray(response?.data?.units)
          ? response.data.units
          : Array.isArray(response?.data)
            ? response.data
            : [];
        this.quantityUnits = units.map(this.normalizeQuantityPackaging);
      } catch (error) {
        console.error("Failed to load packaging units", error);
        this.quantityUnits = [];
        toast.error("Failed to load quantity packaging");
      } finally {
        this.loadingQuantityUnits = false;
      }
    },
    customQuantityUnitsEndpoint(unitId = null) {
      const base = `/v1/store/${this.selectedStoreId}/quantity/units/custom`;
      return unitId ? `${base}/${unitId}` : base;
    },
    normalizeQuantityPackaging(unit = {}) {
      const factor = Number(
        unit.conversion_factor_to_base ??
        unit.conversionFactorToBase ??
        unit.factor ??
        0,
      );
      const baseUnit = unit.base_unit || unit.baseUnit || unit.parent_unit || unit.parentUnit || "U";
      const name = unit.label || unit.name || unit.display_name || unit.unit || "Packaging";
      return {
        ...unit,
        id: unit.id,
        name,
        label: name,
        unit: unit.unit || unit.unit_code || unit.code || name,
        baseUnit,
        factor,
        factorLabel: Number.isFinite(factor) && factor > 0
          ? `${this.formatQuantity(factor)} ${baseUnit}`
          : "-",
        description: unit.description || "",
      };
    },
    resetPackagingForm() {
      this.packagingForm = {
        label: "",
        factor: "",
        baseUnit: "U",
        description: "",
      };
    },
    openPackagingModal(packaging = null) {
      this.editingPackaging = packaging;
      this.packagingForm = packaging
        ? {
          label: packaging.label || packaging.name || "",
          factor: packaging.factor ? String(packaging.factor) : "",
          baseUnit: packaging.baseUnit || "U",
          description: packaging.description || "",
        }
        : {
          label: "",
          factor: "",
          baseUnit: "U",
          description: "",
        };
      this.showPackagingModal = true;
    },
    async savePackaging() {
      if (!this.selectedStoreId || this.savingPackaging) {
        return;
      }

      const label = String(this.packagingForm.label || "").trim();
      const factor = Number(this.packagingForm.factor);
      if (!label || !Number.isFinite(factor) || factor <= 0) {
        toast.error("Enter a packaging name and a positive unit count");
        return;
      }

      this.savingPackaging = true;
      const payload = {
        label,
        base_unit: this.packagingForm.baseUnit || "U",
        factor,
        description: String(this.packagingForm.description || "").trim(),
      };

      try {
        const unitId = this.editingPackaging?.id;
        if (unitId) {
          await http.patch(this.customQuantityUnitsEndpoint(unitId), payload);
        } else {
          await http.post(this.customQuantityUnitsEndpoint(), payload);
        }
        toast.success("Quantity packaging saved");
        this.showPackagingModal = false;
        this.resetPackagingForm();
        this.editingPackaging = null;
        await this.getQuantityUnits();
      } catch (error) {
        console.error("Failed to save packaging", error);
        toast.error(
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to save quantity packaging",
        );
      } finally {
        this.savingPackaging = false;
      }
    },
    confirmDeletePackaging(packaging) {
      this.packagingToDelete = packaging;
      this.showDeletePackagingConfirm = true;
    },
    async deletePackaging() {
      const unitId = this.packagingToDelete?.id;
      if (!this.selectedStoreId || !unitId || this.deletingPackaging) {
        return;
      }

      this.deletingPackaging = true;
      try {
        await http.delete(this.customQuantityUnitsEndpoint(unitId));
        toast.success("Quantity packaging deleted");
        this.showDeletePackagingConfirm = false;
        this.packagingToDelete = null;
        await this.getQuantityUnits();
      } catch (error) {
        console.error("Failed to delete packaging", error);
        toast.error(
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to delete quantity packaging",
        );
      } finally {
        this.deletingPackaging = false;
      }
    },
    mapRemovalRows(items = []) {
      return items.map((item) => ({
        id: item.id,
        created_at: item.created_at,
        product_name: item.product?.name,
        quantity: `${item.quantity_removed} ${item?.product?.default_quantity_unit ||
          item?.product?.quantity_unit ||
          "U"
          }`,
        reason:
          item.removal_reason?.name ||
          (item.created_via_transfer ? "Transfer" : "-"),
        status: item.status,
      }));
    },
    syncRemovalPagination() {
      const pagination = this.removalsPagination || {};
      this.totalCatalog = pagination.total || 0;
      this.totalPages = pagination.pages || 0;
      this.currentPage = pagination.page || 1;
    },
    async handleRemovalFilterChange() {
      this.currentPage = 1;
      await this.getRemovals();
    },
    async clearRemovalFilters() {
      this.resetRemovalFilters();
      this.currentPage = 1;
      await this.getRemovals();
    },
    getRemovalReportFilename(contentDisposition) {
      const filenameMatch = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(
        contentDisposition || "",
      );

      if (filenameMatch?.[1]) {
        return decodeURIComponent(filenameMatch[1].replace(/"/g, ""));
      }

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-");
      return `inventory_removals_${timestamp}.csv`;
    },
    async downloadRemovalReport() {
      if (!this.selectedStoreId) {
        return;
      }

      this.downloadingRemovalReport = true;
      try {
        const response = await http.get(
          `/v2/store/inventory/${this.selectedStoreId}/removals/report`,
          {
            params: this.buildRemovalFilters(),
            responseType: "blob",
          },
        );

        const downloadUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = this.getRemovalReportFilename(
          response.headers?.["content-disposition"],
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        toast.success("Removal report download started");
      } catch (error) {
        console.error("Failed to download removal report", error);
        toast.error("Failed to download removal report");
      } finally {
        this.downloadingRemovalReport = false;
      }
    },
    async fetchStoreGroups() {
      if (!this.selectedStoreId) {
        this.storeGroups = [];
        this.selectedGroupId = "";
        return;
      }
      try {
        // Use Vuex action
        await this.fetchStoreGroupsAction({
          storeId: this.selectedStoreId,
        });

        // Map fresh data
        const items = this.storeGroupsFromStore || [];
        this.storeGroups = items;

        // Auto-select default group or first group if none selected
        if (items.length > 0 && !this.selectedGroupId) {
          // Get current store's default group
          const currentStore = this.$store.getters.getUserStores?.find(
            (s) => s.id === this.selectedStoreId,
          );
          const defaultGroupId = currentStore?.default_group_id;

          // Select default group if set and exists in list, otherwise first group
          if (defaultGroupId && items.some((g) => g.id === defaultGroupId)) {
            this.selectedGroupId = defaultGroupId;
          } else {
            this.selectedGroupId = items[0].id;
          }

          // Load stores for filter if on transfer_requests or transfers tab
          if (
            this.activeTab === "transfer_requests" ||
            this.activeTab === "transfers"
          ) {
            await this.loadGroupStoresForFilter();
          }
        }
      } catch (e) {
        console.error("Failed to fetch store groups", e);
      }
    },
    async getTransfers() {
      if (!this.selectedGroupId) {
        this.transfers = [];
        this.totalCatalog = 0;
        this.totalPages = 0;
        return;
      }

      try {
        // Build filters
        const filters = {};
        const fromStoreFilter =
          this.transferStoreFilter === "all"
            ? null
            : this.transferStoreFilter || this.selectedStoreId;
        if (fromStoreFilter) {
          filters.from_store_id = fromStoreFilter;
        }

        console.log("📝 Fetching transfers with filters:", filters);

        // Use Vuex action
        await this.fetchTransfersAction({
          groupId: this.selectedGroupId,
          filters,
          page: this.currentPage,
          perPage: this.catalogPerPage,
        });

        // Map for UI display from store
        const items = this.transfersFromStore || [];
        this.transfers = items.map((t) => ({
          id: t.local_transfer_id || t.id,
          from: this.storeName(t.from_store_id),
          to: this.storeName(t.to_store_id),
          items_count: Array.isArray(t.items) ? t.items.length : 0,
          status: t.status,
          created_at: t.created_at,
          _raw: t,
        }));

        // Update pagination from store
        const pagination = this.transfersPagination || {};
        this.totalCatalog = pagination.total || 0;
        this.totalPages = pagination.pages || 0;
        this.currentPage = pagination.page || 1;
      } catch (e) {
        console.error("Failed to fetch transfers", e);
        toast.error("Failed to load transfers");
      }
    },
    async getTransferRequests() {
      if (!this.selectedGroupId) {
        this.transferRequests = [];
        this.totalCatalog = 0;
        this.totalPages = 0;
        return;
      }

      try {
        // Build filters
        const filters = {};
        const otherStoreFilter =
          this.transferRequestStoreFilter === "all"
            ? null
            : this.transferRequestStoreFilter || this.selectedStoreId;
        if (otherStoreFilter) {
          filters.other_store_id = otherStoreFilter;
        }

        console.log("📝 Fetching transfer requests with filters:", filters);

        // Use Vuex action
        await this.fetchTransferRequestsAction({
          groupId: this.selectedGroupId,
          filters,
          page: this.currentPage,
          perPage: this.catalogPerPage,
        });

        // Map for UI display
        const items = this.transferRequestsFromStore || [];
        this.transferRequests = items.map((r) => ({
          id: r.local_transfer_request_id || r.id,
          from: this.storeName(r.store_id),
          to: this.storeName(r.other_store_id),
          items_count: (r.items || []).length,
          status: r.status,
          created_at: r.created_at,
          _raw: r,
        }));

        // Update pagination from store
        const pagination = this.requestsPagination || {};
        this.totalCatalog = pagination.total || 0;
        this.totalPages = pagination.pages || 0;
        this.currentPage = pagination.page || 1;
      } catch (e) {
        console.error("Failed to fetch transfer requests", e);
        toast.error("Failed to load transfer requests");
      }
    },
    async getRemovals() {
      if (!this.selectedStoreId) return;

      try {
        await this.ensureRemovalReasonsLoaded();

        // Use Vuex action
        await this.fetchRemovalsAction({
          storeId: this.selectedStoreId,
          filters: this.buildRemovalFilters(),
          page: this.currentPage,
          perPage: this.catalogPerPage,
        });

        const items = this.removalsFromStore || [];
        this.mergeRemovalReasons(items);
        this.removals = this.mapRemovalRows(items);
        this.syncRemovalPagination();
      } catch (e) {
        console.error("Failed to fetch removals", e);
        toast.error("Failed to load removals");
      }
    },
    handleViewTransfer(row) {
      const raw = row?._raw || null;
      if (!raw) {
        this.selectedTransferRaw = { id: row?.id };
      } else {
        this.selectedTransferRaw = raw;
      }
      this.showTransferDetails = true;
    },
    handleViewTransferRequest(row) {
      const raw = row?._raw || null;
      if (!raw) {
        this.selectedRequestRaw = { id: row?.id };
      } else {
        this.selectedRequestRaw = raw;
      }
      this.showRequestDetails = true;
    },
    async handleStartTransferRequest(rowOrRaw, note = null) {
      const id = rowOrRaw?._raw?.id || rowOrRaw?.id || rowOrRaw;
      if (!this.selectedGroupId || !id) return;
      try {
        // Use Vuex action
        await this.updateRequestStatusAction({
          groupId: this.selectedGroupId,
          requestId: id,
          status: "IN_FULFILLMENT",
          note,
        });

        toast.success("Request moved to fulfillment");
        this.showRequestDetails = false;
        await this.getTransferRequests();
      } catch (e) {
        const msg =
          e?.response?.data?.error?.message || "Failed to start fulfillment";
        toast.error(msg);
      }
    },
    async handleCompleteTransferRequest(rowOrRaw, note = null) {
      const id = rowOrRaw?._raw?.id || rowOrRaw?.id || rowOrRaw;
      if (!this.selectedGroupId || !id) return;
      try {
        // Use Vuex action
        await this.updateRequestStatusAction({
          groupId: this.selectedGroupId,
          requestId: id,
          status: "COMPLETED",
          note,
        });

        toast.success("Transfer completed");
        this.showRequestDetails = false;
        await this.getTransferRequests();
        await this.getTransfers();
      } catch (e) {
        const msg =
          e?.response?.data?.error?.message || "Failed to complete transfer";
        toast.error(msg);
      }
    },
    storeName(storeId) {
      if (!storeId) return "-";
      // storeGroups list contains group objects; get stores via group stores endpoint when needed
      // For now, try to resolve from user's stores
      const s = (this.stores || []).find(
        (x) => String(x.id) === String(storeId),
      );
      return s?.name || `Store #${storeId}`;
    },
    formatDate(d) {
      if (!d) return "";
      try {
        const dt = new Date(d);
        return dt.toLocaleString();
      } catch {
        return String(d);
      }
    },
    async handleGroupChange() {
      this.currentPage = 1;
      this.transferRequestStoreFilter = ""; // Reset store filter
      this.transferStoreFilter = ""; // Reset transfers store filter

      // Load stores for the selected group to populate filter
      if (
        this.selectedGroupId &&
        (this.activeTab === "transfer_requests" ||
          this.activeTab === "transfers")
      ) {
        await this.loadGroupStoresForFilter();
      }

      this.handleDataChange(this.searchQuery);
    },
    async loadGroupStoresForFilter() {
      if (!this.selectedGroupId) {
        this.groupStoresForFilter = [];
        return;
      }

      try {
        console.log(
          "🔄 Loading stores for group filter:",
          this.selectedGroupId,
        );

        // Use Vuex action
        await this.fetchGroupStoresAction({
          groupId: this.selectedGroupId,
        });

        // Map fresh data
        const stores = this.groupStoresFromStore || [];
        this.groupStoresForFilter = stores.map((s) => ({
          id: s.store_id,
          name: s.name,
        }));

        console.log("✅ Loaded stores for filter:", this.groupStoresForFilter);
      } catch (error) {
        console.error("❌ Failed to load group stores for filter:", error);
        this.groupStoresForFilter = [];
      }
    },
    async refreshActiveList() {
      // Force refresh bypassing cache
      if (this.activeTab === "removals" && this.selectedStoreId) {
        await this.ensureRemovalReasonsLoaded();
        await this.fetchRemovalsAction({
          storeId: this.selectedStoreId,
          filters: this.buildRemovalFilters(),
          page: this.currentPage,
          perPage: this.catalogPerPage,
          forceRefresh: true, // Bypass cache
        });

        const items = this.removalsFromStore || [];
        this.mergeRemovalReasons(items);
        this.removals = this.mapRemovalRows(items);
        this.syncRemovalPagination();
      } else if (this.activeTab === "transfers" && this.selectedGroupId) {
        // Force refresh transfers
        await this.fetchTransfersAction({
          groupId: this.selectedGroupId,
          filters: this.transferFilters,
          page: this.currentPage,
          perPage: this.catalogPerPage,
          forceRefresh: true, // Bypass cache
        });

        // Map fresh data
        const items = this.transfersFromStore || [];
        this.transfers = items.map((t) => ({
          id: t.local_transfer_id,
          from_store: t.from_store?.name,
          to_store: t.to_store?.name,
          items: `${t.items?.length || 0} items`,
          status: t.status,
          created_at: t.created_at,
        }));

        const pagination = this.transfersPagination;
        this.totalCatalog = pagination.total || 0;
        this.totalPages = pagination.pages || 0;
        this.currentPage = pagination.page || 1;
      } else if (
        this.activeTab === "transfer_requests" &&
        this.selectedGroupId
      ) {
        // Force refresh transfer requests
        await this.fetchTransferRequestsAction({
          groupId: this.selectedGroupId,
          filters: this.requestFilters,
          page: this.currentPage,
          perPage: this.catalogPerPage,
          forceRefresh: true, // Bypass cache
        });

        // Map fresh data
        const items = this.transferRequestsFromStore || [];
        this.transferRequests = items.map((r) => ({
          id: r.local_transfer_request_id,
          from_store: r.store?.name,
          to_store: r.other_store?.name,
          items: `${r.items?.length || 0} items`,
          status: r.status,
          created_at: r.created_at,
        }));

        const pagination = this.requestsPagination;
        this.totalCatalog = pagination.total || 0;
        this.totalPages = pagination.pages || 0;
        this.currentPage = pagination.page || 1;
      } else if (this.activeTab === "inventory" && this.selectedStoreId) {
        // Force refresh inventory
        await this.fetchInventoryAction({
          storeId: this.selectedStoreId,
          filters: {
            include: "has_rra_ebm_metadata",
            query: this.searchQuery,
            sort: this.getSortParameter() || "name",
          },
          page: this.currentPage,
          perPage: this.catalogPerPage,
          forceRefresh: true, // Bypass cache
        });

        // Map fresh data
        const items = this.inventoryFromStore || [];
        this.inventory = items.map(this.mapInventoryCatalogItem);

        const pagination = this.inventoryPagination;
        this.totalCatalog = pagination.total || 0;
        this.totalPages = pagination.pages || 0;
        this.currentPage = pagination.page || 1;

        // Fetch price analytics for refreshed inventory
        await this.fetchPriceAnalytics();
        await this.loadInventoryDeficitCount();
      } else if (this.activeTab === "products" && this.selectedStoreId) {
        // Force refresh products
        await this.fetchProductsAction({
          storeId: this.selectedStoreId,
          filters: {
            include: "has_rra_ebm_metadata",
            query: this.searchQuery,
          },
          page: this.currentPage,
          perPage: this.catalogPerPage,
          forceRefresh: true, // Bypass cache
        });

        // Map fresh data
        const items = this.productsFromStore || [];
        this.products = this.mapProductRows(items);

        const pagination = this.productsPagination;
        this.totalCatalog = pagination.total || 0;
        this.totalPages = pagination.pages || 0;
        this.currentPage = pagination.page || 1;
      } else if (this.activeTab === "services" && this.selectedStoreId) {
        // Force refresh services
        await this.fetchServicesAction({
          storeId: this.selectedStoreId,
          filters: {
            include: "has_rra_ebm_metadata",
            query: this.searchQuery,
          },
          page: this.currentPage,
          perPage: this.catalogPerPage,
          forceRefresh: true, // Bypass cache
        });

        // Map fresh data
        const items = this.servicesFromStore || [];
        this.services = this.mapServiceRows(items);

        const pagination = this.servicesPagination;
        this.totalCatalog = pagination.total || 0;
        this.totalPages = pagination.pages || 0;
        this.currentPage = pagination.page || 1;
      } else if (this.activeTab === "purchases" && this.selectedStoreId) {
        // Force refresh purchases
        const filters = {
          query: this.searchQuery,
          sort: this.getSortParameter(),
        };

        // Add expiration filter using standard filter syntax
        if (this.expirationFilter === "hide_expired") {
          const now = new Date().toISOString();
          filters["expiration_date[gt]"] = now;
        } else if (this.expirationFilter === "expired_only") {
          const now = new Date().toISOString();
          filters["expiration_date[lte]"] = now;
        }

        await this.fetchPurchasesAction({
          storeId: this.selectedStoreId,
          filters,
          page: this.currentPage,
          perPage: this.catalogPerPage,
          forceRefresh: true, // Bypass cache
        });

        // Map fresh data
        const items = this.purchasesFromStore || [];
        this.purchases = items.map((p) => ({
          id: p?.product_purchase_id,
          product_id: p?.product_id,
          name: p?.name,
          sku: p?.item_sku || "",
          barcode: p?.barcode || "",
          quantity: p?.quantity,
          unit_price: p?.unit_price,
          expiration: p?.expiration,
          created_at: p?.created_at,
        }));

        const pagination = this.purchasesPagination;
        this.totalCatalog = pagination.total || 0;
        this.totalPages = pagination.pages || 0;
        this.currentPage = pagination.page || 1;
      } else {
        // For other tabs, use existing handleDataChange
        this.handleDataChange(this.searchQuery);
      }
    },
    async loadInventoryDeficitCount() {
      if (!this.selectedStoreId || !this.inventoryCommitmentsEnabled) {
        this.openDeficitCount = 0;
        return;
      }
      try {
        const response = await http.get(
          api.inventory_commitment_deficits(this.selectedStoreId),
        );
        this.openDeficitCount = Number(response?.data?.total || 0);
      } catch (error) {
        console.error("Failed to load inventory shortages", error);
        this.openDeficitCount = 0;
      }
    },
    async loadInventoryCommitmentSettings(storeId = this.selectedStoreId) {
      this.inventorySettings = null;
      this.inventoryCommitmentsEnabled = false;
      this.openDeficitCount = 0;
      this.showInventoryReconciliation = false;
      if (!storeId) return;

      try {
        const response = await http.get(api.inventory_settings(storeId));
        if (String(this.selectedStoreId) !== String(storeId)) return;
        this.inventorySettings = response?.data || null;
        this.inventoryCommitmentsEnabled =
          response?.data?.effective_commitment_tracking_enabled === true;
      } catch (error) {
        console.error("Failed to load inventory commitment settings", error);
      }
    },
    async handleInventoryPolicySaved(settings) {
      this.inventorySettings = settings;
      this.inventoryCommitmentsEnabled =
        settings?.effective_commitment_tracking_enabled === true;
      await this.refreshActiveList();
      await this.fetchCatalogStats();
    },
    openInventoryReconciliation() {
      if (!this.inventoryCommitmentsEnabled) return;
      this.showInventoryReconciliation = true;
    },
    async handleInventoryReconciled() {
      await this.refreshActiveList();
      await this.fetchCatalogStats();
    },
    async rejectTransferRequest(req, note = null) {
      if (!this.selectedGroupId) return;
      try {
        // Use Vuex action
        await this.updateRequestStatusAction({
          groupId: this.selectedGroupId,
          requestId: req.id,
          status: "REJECTED",
          note,
        });

        toast.success("Request rejected");
        this.showRequestDetails = false;
        await this.getTransferRequests();
      } catch (e) {
        const msg =
          e?.response?.data?.error?.message || "Failed to reject request";
        toast.error(msg);
      }
    },
    openProductMappings() {
      if (!this.selectedStoreId) return;
      this.$router.push({
        name: "orders_product_mappings",
        params: { storeId: this.selectedStoreId },
      });
    },
    getSortParameter() {
      // Apply sorting for inventory and purchases tabs only
      if (["inventory", "purchases"].includes(this.activeTab)) {
        let sortValue =
          typeof this.sortBy === "string" ? this.sortBy.trim() : "";
        // Prevent invalid expiration sorts on inventory tab
        if (
          this.activeTab === "inventory" &&
          sortValue.includes("expiration")
        ) {
          sortValue = "name";
        }
        if (!sortValue) {
          sortValue =
            this.activeTab === "purchases" ? "expiration_date" : "name";
        }
        return sortValue;
      }
      return undefined; // No sorting for products/services tabs
    },
    async handleTabChange(tab) {
      // Guard: ignore accidental DOM events
      if (typeof tab !== "string") return;
      this.activeTab = tab;
      this.selectedCatalog = [];
      this.currentPage = 1;
      this.searchQuery = ""; // Clear search when switching tabs

      // Reset sort to default when switching away from inventory tab
      if (tab !== "inventory") {
        this.sortBy = "name";
      }

      // Load store groups when switching to transfers context
      if (["transfers", "transfer_requests"].includes(tab)) {
        await this.fetchStoreGroups();

        // Load group stores for filter if group is already selected
        if (
          (tab === "transfer_requests" || tab === "transfers") &&
          this.selectedGroupId
        ) {
          await this.loadGroupStoresForFilter();
        }
      }

      await this.handleDataChange(this.searchQuery);
      await this.updateUrlParams();
    },
    handleSelectAll(isSelected) {
      if (isSelected) {
        const catalog =
          this.activeTab === "products"
            ? this.products
            : this.activeTab === "services"
              ? this.services
              : this.inventory;
        this.selectedCatalog = catalog.filter((item) => item.sku);
      } else {
        this.selectedCatalog = [];
      }
    },
    handleViewItemHistory(item) {
      this.itemHistoryId =
        this.activeTab === "purchases"
          ? item.product_id || item.id
          : item.id;
      this.itemHistoryType =
        this.activeTab === "services" ? "service" : "product";
      this.itemHistoryName = item.name || "";
      this.showItemHistoryModal = true;
    },
    handleSelectItem(item, isSelected) {
      if (isSelected) {
        if (
          !this.selectedCatalog.some(
            (selectedItem) => selectedItem.id === item.id,
          )
        ) {
          this.selectedCatalog.push(item);
        }
      } else {
        this.selectedCatalog = this.selectedCatalog.filter(
          (selectedItem) => selectedItem.id !== item.id,
        );
      }
    },
    handleEditItem(item) {
      this.selectedItem = item;
      this.showEditModal = true;
      this.isCreatingItem = false;
    },
    handleDeleteItem(item) {
      this.showConfirmationModal = true;
      this.confirmationTitle = "Delete Item";
      this.confirmationMessage = `Are you sure you want to delete ${item.name}?`;
      this.confirmationConfirmText = "Delete";
      this.confirmationCancelText = "Cancel";
      this.selectedItem = item;
    },
    handleCloseModal() {
      this.showEditModal = false;
      this.selectedItem = null;
    },
    async refreshCatalogAfterMutation() {
      await this.handleDataChange(this.searchQuery);
      await this.updateUrlParams();
      await this.fetchCatalogStats();
    },
    handleSaveService(updatedItem) {
      if (this.isCreatingItem) {
        this.$store
          .dispatch("addService", { service: updatedItem })
          .then(async () => {
            toast.success("Service added successfully");
            this.showEditModal = false;
            this.selectedItem = null;
            await this.refreshCatalogAfterMutation();
          })
          .catch((error) => {
            toast.error(
              error?.response?.data?.message || "Failed to add service",
            );
          });
      } else {
        this.$store
          .dispatch("editService", { service: updatedItem })
          .then(async () => {
            toast.success("Service updated successfully");
            this.showEditModal = false;
            this.selectedItem = null;
            await this.refreshCatalogAfterMutation();
          })
          .catch((error) => {
            toast.error(
              error?.response?.data?.message || "Failed to update service",
            );
          });
      }
    },
    handleSaveProduct(updatedItem) {
      if (this.isCreatingItem) {
        this.$store
          .dispatch("addProduct", { product: updatedItem })
          .then(async () => {
            toast.success("Product added successfully");
            this.showEditModal = false;
            this.selectedItem = null;
            await this.refreshCatalogAfterMutation();
          })
          .catch((error) => {
            toast.error(
              error?.response?.data?.message || "Failed to add product",
            );
          });
      } else {
        this.$store
          .dispatch("editProduct", { product: updatedItem })
          .then(async () => {
            toast.success("Product updated successfully");
            this.showEditModal = false;
            this.selectedItem = null;
            await this.refreshCatalogAfterMutation();
          })
          .catch((error) => {
            toast.error(
              error?.response?.data?.message || "Failed to update product",
            );
          });
      }
    },
    handleSaveItem(updatedItem) {
      if (this.activeTab === "products" || this.activeTab === "inventory") {
        this.handleSaveProduct(updatedItem);
      } else {
        this.handleSaveService(updatedItem);
      }
    },
    formatQuantity(quantity) {
      const cleaned = quantity.toString().replace(/(\.\d{0,2})\d*$/, "$1");
      return `${cleaned}`;
    },
    formatPrice(price) {
      const formattedPrice = price?.unit_price
        ?.toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return `${price?.currency || "RWF"} ${formattedPrice || "0.00"}`;
    },
    mapServiceRows(items) {
      return (items || []).map((service) => {
        const salePrice = service?.service_sale_price || {
          unit_price: 0,
          currency: "RWF",
        };
        const formattedPrice = this.formatPrice(salePrice);
        return {
          id: service.id,
          name: service.name,
          description: service.description,
          sku: service.item_sku || "",
          price: formattedPrice,
          priceRaw: salePrice,
          has_rra_ebm_metadata: service.has_rra_ebm_metadata || false,
        };
      });
    },
    mapProductRows(items) {
      return (items || []).map((product) => {
        const salePrice = product?.price || { unit_price: 0, currency: "RWF" };
        const formattedPrice = this.formatPrice(salePrice);
        return {
          id: product.id,
          productId: product.id,
          name: product.name,
          sku: product.item_sku || "",
          barcode: product.barcode || "",
          price: formattedPrice,
          priceRaw: salePrice,
          quantity_unit: product.quantity_unit || product.default_quantity_unit || "U",
          default_quantity_unit: product.default_quantity_unit || product.quantity_unit || "U",
          salePrices: product.sale_prices || [],
          sale_prices: product.sale_prices || [],
          originalData: product,
          has_rra_ebm_metadata: product.has_rra_ebm_metadata || false,
        };
      });
    },
    async getProducts(searchQuery = "") {
      if (!this.selectedStoreId) return;

      try {
        const filters = {
          include: "has_rra_ebm_metadata",
        };

        if (typeof searchQuery === "string" && searchQuery.trim().length > 0) {
          filters.query = searchQuery;
        }

        // Use Vuex action
        await this.fetchProductsAction({
          storeId: this.selectedStoreId,
          filters,
          page: this.currentPage,
          perPage: this.catalogPerPage,
        });

        // Map fresh data
        const items = this.productsFromStore || [];
        this.products = this.mapProductRows(items);

        const pagination = this.productsPagination;
        this.totalCatalog = pagination.total || 0;
        this.totalPages = pagination.pages || 0;
        this.currentPage = pagination.page || 1;
      } catch (e) {
        console.error("Failed to fetch products", e);
      }
    },
    async getServices(searchQuery = "") {
      if (!this.selectedStoreId) return;

      try {
        const filters = {
          include: "has_rra_ebm_metadata",
        };

        if (typeof searchQuery === "string" && searchQuery.trim().length > 0) {
          filters.query = searchQuery;
        }

        // Use Vuex action
        await this.fetchServicesAction({
          storeId: this.selectedStoreId,
          filters,
          page: this.currentPage,
          perPage: this.catalogPerPage,
        });

        // Map fresh data
        const items = this.servicesFromStore || [];
        this.services = this.mapServiceRows(items);

        const pagination = this.servicesPagination;
        this.totalCatalog = pagination.total || 0;
        this.totalPages = pagination.pages || 0;
        this.currentPage = pagination.page || 1;
      } catch (e) {
        console.error("Failed to fetch services", e);
      }
    },
    async getInventory(searchQuery = "") {
      if (!this.selectedStoreId) return;

      try {
        const filters = {
          include: "has_rra_ebm_metadata",
        };

        if (typeof searchQuery === "string" && searchQuery.trim().length > 0) {
          filters.query = searchQuery;
        }

        // Only add sort parameter if it's defined
        const sortParam = this.getSortParameter();
        filters.sort =
          typeof sortParam === "string" && sortParam.trim().length
            ? sortParam
            : "name";

        // Use Vuex action
        await this.fetchInventoryAction({
          storeId: this.selectedStoreId,
          filters,
          page: this.currentPage,
          perPage: this.catalogPerPage,
        });

        // Map fresh data
        const items = this.inventoryFromStore || [];
        this.inventory = items.map(this.mapInventoryCatalogItem);

        const pagination = this.inventoryPagination;
        this.totalCatalog = pagination.total || 0;
        this.totalPages = pagination.pages || 0;
        this.currentPage = pagination.page || 1;

        // Fetch price analytics for all inventory items
        await this.fetchPriceAnalytics();
        await this.loadInventoryDeficitCount();
      } catch (e) {
        console.error("Failed to fetch inventory", e);
      }
    },
    async fetchPriceAnalytics() {
      if (!this.selectedStoreId || this.inventory.length === 0) {
        console.log("⏭️ Skipping price analytics - no store or inventory");
        return;
      }

      console.log("🔍 Fetching price analytics for inventory items...");
      this.loadingPriceAnalytics = true;
      try {
        // Get all product IDs from current inventory
        const productIds = this.inventory
          .map((item) => item.id)
          .filter((id) => id)
          .join(",");

        if (!productIds) {
          console.log("⚠️ No product IDs found in inventory");
          this.loadingPriceAnalytics = false;
          return;
        }

        console.log(`📊 Fetching analytics for products: ${productIds}`);

        // Use http directly (imported at top of file)
        const url = `/v2/store/${this.selectedStoreId}/products/purchase-price-ranges?product_ids=${productIds}`;
        const response = await http.get(url);

        // Store analytics data indexed by product ID
        this.priceAnalytics = response.data || {};
        console.log(
          `✅ Loaded price analytics for ${Object.keys(this.priceAnalytics).length
          } products`,
          this.priceAnalytics,
        );
      } catch (error) {
        console.error("❌ Failed to fetch price analytics:", error);
        // Don't show error to user - analytics is supplementary
      } finally {
        this.loadingPriceAnalytics = false;
      }
    },
    async getPurchases(searchQuery = "") {
      if (!this.selectedStoreId) return;

      try {
        const filters = {};

        if (typeof searchQuery === "string" && searchQuery.trim().length > 0) {
          filters.query = searchQuery;
        }

        // Sorting for purchases
        const sortParam = this.getSortParameter();
        if (sortParam) {
          filters.sort = sortParam;
        }

        // Expiration filter using standard filter syntax
        if (this.expirationFilter === "hide_expired") {
          // Show only non-expired items: expiration_date > now (nulls handled by backend)
          const now = new Date().toISOString();
          filters["expiration_date[gt]"] = now;
        } else if (this.expirationFilter === "expired_only") {
          // Show only expired items: expiration_date <= now
          const now = new Date().toISOString();
          filters["expiration_date[lte]"] = now;
        }

        // Use Vuex action
        await this.fetchPurchasesAction({
          storeId: this.selectedStoreId,
          filters,
          page: this.currentPage,
          perPage: this.catalogPerPage,
        });

        // Map fresh data
        const items = this.purchasesFromStore || [];
        this.purchases = items.map((p) => ({
          id: p?.product_purchase_id,
          product_id: p?.product_id,
          name: p?.name,
          sku: p?.item_sku || "",
          barcode: p?.barcode || "",
          quantity: p?.quantity,
          unit_price: p?.unit_price,
          expiration: p?.expiration,
          created_at: p?.created_at,
        }));

        const pagination = this.purchasesPagination;
        this.totalCatalog = pagination.total || 0;
        this.totalPages = pagination.pages || 0;
        this.currentPage = pagination.page || 1;
      } catch (e) {
        console.error("Failed to fetch purchases", e);
      }
    },
    async handleNextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        await this.handleDataChange(this.searchQuery);
        await this.updateUrlParams();
      }
    },
    async handlePreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        await this.handleDataChange(this.searchQuery);
        await this.updateUrlParams();
      }
    },
    handleRouteChange(route) {
      const storeId = route.params.storeId;
      const currentStoreId = this.$store.state.dashboard?.selectedStore;

      if (storeId && String(storeId) !== String(currentStoreId)) {
        const stores = this.$store.getters.getUserStores || [];
        const storeExists = stores.some(
          (s) => String(s.id) === String(storeId),
        );
        if (storeExists)
          this.$store.commit("SET_SELECTED_STORE", parseInt(storeId));
      }

      const page = parseInt(route.query.page) || 1;
      const perPage = parseInt(route.query.per_page) || 10;
      const search = (route.query.query || "").toString();
      const tab = route.query.tab || "inventory";
      const sortBy =
        route.query.sort || (tab === "purchases" ? "expiration_date" : "name");

      this.currentPage = page;
      this.catalogPerPage = perPage;
      this.activeTab = tab;
      this.sortBy = sortBy;
      this.searchQuery = search;

      if (this.selectedStoreId && !this.isUpdatingRoute) {
        this.handleDataChange(this.searchQuery);
      }

      if (this.isUpdatingRoute) {
        this.isUpdatingRoute = false;
      }
    },
    async updateUrlParams() {
      if (!this.selectedStoreId) {
        return;
      }
      // Ensure we have valid values, not Event objects
      const page = typeof this.currentPage === "number" ? this.currentPage : 1;
      const perPage =
        typeof this.catalogPerPage === "number" ? this.catalogPerPage : 10;
      const tab =
        typeof this.activeTab === "string" ? this.activeTab : "inventory";
      const search =
        typeof this.searchQuery === "string" ? this.searchQuery : "";
      const sortBy = typeof this.sortBy === "string" ? this.sortBy : "name";

      const query = {
        page: page,
        per_page: perPage,
        tab: tab,
      };
      if (typeof search === "string" && search.trim().length > 0) {
        query.query = search;
      }

      if (tab === "inventory" || tab === "purchases") {
        query.sort = sortBy;
        if (tab === "inventory") {
          query.include = "has_rra_ebm_metadata";
        }
      }

      try {
        this.isUpdatingRoute = true;
        await this.$router.replace({
          name: "catalog",
          params: { storeId: this.selectedStoreId },
          query: query,
        });
      } catch (err) {
        console.error("Failed to update catalog route params", err);
      } finally {
        this.isUpdatingRoute = false;
      }
    },
    async handleSearch(searchQuery) {
      // Ensure we receive a string, not an Event object
      const query = typeof searchQuery === "string" ? searchQuery : "";
      this.searchQuery = query;
      this.currentPage = 1;
      await this.handleDataChange(query);
      await this.updateUrlParams();
    },
    handleOpenModal() {
      this.showEditModal = true;
      this.selectedItem = null;
      this.isCreatingItem = true;
      this.selectedItem = {
        name: "",
        sku: "",
      };
      if (this.activeTab === "products" || this.activeTab === "inventory") {
        this.selectedItem.barcode = "";
      } else {
        this.selectedItem.description = "";
      }
    },
    handleConfirmDelete() {
      if (this.activeTab === "products" || this.activeTab === "inventory") {
        this.$store
          .dispatch("deleteProduct", {
            product_id: this.selectedItem.productId,
          })
          .then(async () => {
            toast.success("Product deleted successfully");
            this.showConfirmationModal = false;
            this.selectedItem = null;
            await this.refreshCatalogAfterMutation();
          })
          .catch((error) => {
            toast.error(
              error?.response?.data?.error?.message ||
              "Failed to delete product",
            );
          });
      } else {
        this.$store
          .dispatch("deleteService", { service_id: this.selectedItem.id })
          .then(async () => {
            toast.success("Service deleted successfully");
            this.showConfirmationModal = false;
            this.selectedItem = null;
            await this.refreshCatalogAfterMutation();
          })
          .catch((error) => {
            toast.error(
              error?.response?.data?.error?.message ||
              "Failed to delete service",
            );
          });
      }
    },
    async handlePerPageChange(newPerPage) {
      this.catalogPerPage = newPerPage;
      this.currentPage = 1;
      await this.handleDataChange(this.searchQuery);
      await this.updateUrlParams();
    },
    handleOpenPriceModal({ type, itemId, itemName, currentPrice, currency, item }) {
      if (!["product", "service"].includes(type)) {
        console.warn(
          "Unsupported price modal type:",
          type,
          "Expected 'product' or 'service'."
        );
        return;
      }
      this.priceModalType = type;
      this.priceModalItemId = itemId;
      this.priceModalItemName = itemName || item?.name || "";
      this.priceModalCurrentPrice = currentPrice || 0;
      this.priceModalCurrency = currency || "RWF";
      this.priceModalItem = item || null;
      this.showPriceModal = true;
    },
    handleClosePriceModal() {
      this.showPriceModal = false;
      this.priceModalType = null;
      this.priceModalItemId = null;
      this.priceModalItemName = "";
      this.priceModalCurrentPrice = 0;
      this.priceModalCurrency = "RWF";
      this.priceModalItem = null;
    },
    async handlePriceModalUpdated({ type, itemId, newPrice, currency, updatedPrices }) {
      if (type === "service") {
        await this.handleServicePriceUpdated({
          serviceId: itemId,
          newPrice,
          currency,
        });
      } else if (type === "product") {
        await this.handlePurchasePriceUpdated({
          productId: itemId,
          newPrice,
          currency,
          updatedPrices,
        });
      } else {
        console.warn("Unsupported price update type:", type);
      }
    },
    normalizeBoolean(value) {
      if (typeof value === "string") {
        return value.toLowerCase() === "true";
      }
      return Boolean(value);
    },
    itemHasEbmMetadata(item) {
      return this.normalizeBoolean(
        item?.has_rra_ebm_metadata ||
        item?.product?.has_rra_ebm_metadata ||
        item?.originalData?.has_rra_ebm_metadata ||
        item?.originalData?.product?.has_rra_ebm_metadata,
      );
    },
    buildUpdatedEbmItemMetadata(metadata, salePrice) {
      const allowedFields = [
        "tin",
        "bhfId",
        "itemCd",
        "itemClsCd",
        "itemTyCd",
        "itemNm",
        "orgnNatCd",
        "pkgUnitCd",
        "qtyUnitCd",
        "taxTyCd",
        "isrcAplcbYn",
        "useYn",
        "dftPrc",
        "grpPrcL1",
        "grpPrcL2",
        "grpPrcL3",
        "grpPrcL4",
        "grpPrcL5",
        "sftyQty",
        "itemStdNm",
        "btchNo",
        "bcd",
        "addInfo",
        "rraModYn",
        "regBhfId",
        "regrNm",
        "regrId",
        "modrNm",
        "modrId",
      ];
      const cleanedMetadata = allowedFields.reduce((result, key) => {
        if (metadata?.[key] !== undefined && metadata?.[key] !== null) {
          result[key] = metadata[key];
        }
        return result;
      }, {});
      const numericSalePrice = Number(salePrice);
      return {
        ...cleanedMetadata,
        dftPrc: numericSalePrice,
        grpPrcL1: numericSalePrice,
        grpPrcL2: numericSalePrice,
        grpPrcL3: numericSalePrice,
        grpPrcL4: numericSalePrice,
        grpPrcL5: numericSalePrice,
        sftyQty: Number(metadata?.sftyQty ?? 0),
      };
    },
    async syncEbmMetadataSalePrice({ type, itemId, item, newPrice }) {
      if (!itemId || !this.itemHasEbmMetadata(item)) {
        return;
      }

      try {
        const endpoint =
          type === "service"
            ? `/v1/store/service/${itemId}?include=rra_ebm_metadata`
            : `/v1/store/product/${itemId}?include=rra_ebm_metadata`;
        const response = await http.get(endpoint);
        const record = response?.data || {};
        const metadata = record.rra_ebm_metadata;

        if (!metadata) {
          return;
        }

        const rraEbmMetadata = this.buildUpdatedEbmItemMetadata(
          metadata,
          newPrice,
        );

        if (type === "service") {
          await http.patch("/v1/store/service", {
            service_id: itemId,
            name: record.name || item?.name,
            description: record.description || item?.description || "",
            item_sku: record.item_sku || item?.sku || item?.item_sku,
            rra_ebm_metadata: rraEbmMetadata,
          });
          return;
        }

        await http.patch("/v1/store/product", {
          product_id: itemId,
          name: record.name || item?.name,
          item_sku: record.item_sku || item?.sku || item?.item_sku,
          barcode: record.barcode || item?.barcode || "",
          description: record.description || item?.description,
          default_quantity_unit:
            record.default_quantity_unit ||
            record.quantity_unit ||
            item?.default_quantity_unit ||
            item?.quantity_unit ||
            item?.product?.default_quantity_unit ||
            item?.product?.quantity_unit ||
            "U",
          rra_ebm_metadata: rraEbmMetadata,
        });
      } catch (error) {
        console.error("Failed to sync EBM metadata sale price", error);
        toast.warning(
          "Sale price updated, but EBM metadata price could not be synced.",
        );
      }
    },
    mergeProductSalePrices(existingPrices = [], updatedPrices = []) {
      const mergedPrices = [...(existingPrices || [])];
      (updatedPrices || []).forEach((updatedPrice) => {
        if (!updatedPrice) return;
        const updatedUnit = String(updatedPrice.price_unit_code || "").toUpperCase();
        const existingIndex = mergedPrices.findIndex((price) => {
          if (updatedPrice.id && price?.id === updatedPrice.id) return true;
          return (
            String(price?.price_unit_code || "").toUpperCase() === updatedUnit
          );
        });

        if (existingIndex >= 0) {
          mergedPrices.splice(existingIndex, 1, updatedPrice);
        } else {
          mergedPrices.push(updatedPrice);
        }
      });

      return mergedPrices.sort((left, right) => {
        if (Boolean(left?.is_default) !== Boolean(right?.is_default)) {
          return left?.is_default ? -1 : 1;
        }
        return String(left?.price_unit_code || "").localeCompare(
          String(right?.price_unit_code || "")
        );
      });
    },
    chooseUpdatedDisplayPrice(currentPrice, updatedPrices = []) {
      if (!updatedPrices?.length) return currentPrice;
      const currentUnit = String(currentPrice?.price_unit_code || "").toUpperCase();
      const matchingCurrentUnit = updatedPrices.find(
        (price) => String(price?.price_unit_code || "").toUpperCase() === currentUnit
      );
      if (matchingCurrentUnit) return matchingCurrentUnit;
      return updatedPrices.find((price) => price?.is_default) || updatedPrices[0];
    },
    applyProductPriceUpdate(row, newPrice, currency, updatedPrices = []) {
      const existingSalePrices = row.salePrices || row.sale_prices || [];
      const mergedSalePrices = this.mergeProductSalePrices(
        existingSalePrices,
        updatedPrices
      );
      row.salePrices = mergedSalePrices;
      row.sale_prices = mergedSalePrices;
      if (row.originalData) {
        row.originalData.sale_prices = mergedSalePrices;
      }

      const currentPrice = row.priceRaw || row.price || {};
      const displayPrice = this.chooseUpdatedDisplayPrice(
        currentPrice,
        updatedPrices
      );
      const updatedPrice = {
        ...currentPrice,
        ...displayPrice,
        unit_price: displayPrice?.unit_price ?? newPrice,
        currency: currency || displayPrice?.currency || currentPrice.currency || "RWF",
      };

      if (row.priceFormatted !== undefined || row.price?.unit_price !== undefined) {
        row.price = updatedPrice;
        row.priceFormatted = this.formatPrice(updatedPrice);
      } else {
        row.priceRaw = updatedPrice;
        row.price = this.formatPrice(updatedPrice);
      }
    },
    async handlePurchasePriceUpdated({ productId, newPrice, currency, updatedPrices = [] }) {
      // Update the price atomically in the inventory array
      const inventoryIndex = this.inventory.findIndex(
        (item) => item.id === productId,
      );
      const productIndex = this.products.findIndex(
        (item) => item.id === productId
      );
      const item = inventoryIndex !== -1
        ? this.inventory[inventoryIndex]
        : productIndex !== -1
          ? this.products[productIndex]
          : null;

      await this.syncEbmMetadataSalePrice({
        type: "product",
        itemId: productId,
        item,
        newPrice,
      });

      if (inventoryIndex !== -1) {
        this.applyProductPriceUpdate(
          this.inventory[inventoryIndex],
          newPrice,
          currency,
          updatedPrices
        );
      }

      if (productIndex !== -1) {
        this.applyProductPriceUpdate(
          this.products[productIndex],
          newPrice,
          currency,
          updatedPrices
        );
      }
    },
    async handleServicePriceUpdated({ serviceId, newPrice, currency }) {
      const serviceIndex = this.services.findIndex(
        (item) => item.id === serviceId
      );
      const item = serviceIndex !== -1 ? this.services[serviceIndex] : null;

      await this.syncEbmMetadataSalePrice({
        type: "service",
        itemId: serviceId,
        item,
        newPrice,
      });

      if (serviceIndex !== -1) {
        const basePrice = this.services[serviceIndex].priceRaw || {
          unit_price: 0,
          currency: "RWF",
        };
        const updatedPrice = {
          ...basePrice,
          unit_price: newPrice,
          currency: currency || basePrice.currency,
        };
        const formattedPrice = this.formatPrice(updatedPrice);
        this.services[serviceIndex].priceRaw = updatedPrice;
        this.services[serviceIndex].price = formattedPrice;
      }
    },
    handleTogglePriceAnalytics(productId) {
      // Close if clicking the same product, open if different
      if (this.openPriceAnalyticsProductId === productId) {
        this.openPriceAnalyticsProductId = null;
      } else {
        this.openPriceAnalyticsProductId = productId;
      }
    },
    handleOpenCopyModal() {
      this.showCopyModal = true;
    },
    handleOpenRequestModal() {
      if (!this.selectedStoreId) {
        toast.error("Select a store before creating a request.");
        return;
      }
      if (!this.selectedCatalog.length) {
        toast.error("Select items to request.");
        return;
      }
      this.showRequestModal = true;
    },
    handleRequestSubmitted() {
      this.showRequestModal = false;
      this.selectedCatalog = [];
      // Toast already shown in modal, just refresh
      // Refresh the transfer requests tab if on that tab
      if (this.activeTab === "transfer_requests") {
        this.handleDataChange(this.searchQuery);
      }
    },
    handleCopyModalClose() {
      this.showCopyModal = false;
      this.selectedCatalog = [];
    },
    // EBM related methods
    async handleSaveToEbm(item) {
      console.log("StoreCatalogView: handleSaveToEbm called with item:", item);
      console.log(
        "StoreCatalogView: Current showEbmModal state:",
        this.showEbmModal,
      );

      this.selectedEbmItem = item;
      this.loadingEbmSettings = true;

      try {
        // Refresh EBM settings before opening modal (user action)
        await this.$store.dispatch("getRraEbmSettings", this.selectedStoreId);

        const ebmSettings = this.$store.getters.rraEbmSettings;
        console.log("StoreCatalogView: EBM settings retrieved:", ebmSettings);

        if (!ebmSettings?.ebm_activated) {
          console.log("StoreCatalogView: EBM not activated, showing error");
          toast.error(
            "EBM is not activated for this store. Please activate EBM first in store settings.",
          );
          this.loadingEbmSettings = false;
          return;
        }

        console.log("StoreCatalogView: Setting showEbmModal to true");
        this.showEbmModal = true;
        console.log(
          "StoreCatalogView: showEbmModal is now:",
          this.showEbmModal,
        );
      } catch (error) {
        console.error("Failed to fetch EBM settings:", error);
        toast.error("Failed to load EBM settings. Please try again.");
      } finally {
        this.loadingEbmSettings = false;
      }
    },
    handleCloseEbmModal() {
      this.showEbmModal = false;
      this.selectedEbmItem = null;
    },
    async handleConfirmSaveToEbm(ebmData) {
      try {
        // Use the existing product/service update endpoints that handle RRA EBM integration
        let response;

        if (this.activeTab === "services") {
          // Update service with EBM data using v2 endpoint
          response = await http.patch(
            `/v2/store/service/${this.selectedEbmItem.id}`,
            {
              item_sku: ebmData.itemCd,
              // Add other EBM-related fields as needed
            },
          );
        } else {
          // Update product with EBM data using v2 endpoint (for both products and inventory)
          response = await http.patch(
            `/v2/store/product/${this.selectedEbmItem.id}`,
            {
              item_sku: ebmData.itemCd,
              // Add other EBM-related fields as needed
            },
          );
        }

        if (response.data) {
          toast.success("Item saved to EBM successfully!");
          this.showEbmModal = false;
          this.selectedEbmItem = null;

          // Refresh the current tab to update the EBM status
          this.handleDataChange(this.searchQuery);
        } else {
          toast.error("Failed to save item to EBM");
        }
      } catch (error) {
        console.error("Failed to save to EBM:", error);
        toast.error(
          error.response?.data?.error?.message || "Failed to save item to EBM",
        );
      }
    },

    // New EBM Data Editor methods
    handleEditEbmData(inventoryItem) {
      console.log(
        "StoreCatalogView: handleEditEbmData called with item:",
        inventoryItem,
      );

      console.log("StoreCatalogView: Inventory item structure:", {
        hasProduct: !!inventoryItem?.product,
        productId: inventoryItem?.product?.id,
        hasPrice: !!inventoryItem?.price,
        priceValue: inventoryItem?.price?.unit_price,
        hasOriginalData: !!inventoryItem?.originalData,
      });

      // First set the inventory item
      this.selectedInventoryItem = inventoryItem;

      // Then open the modal in the next tick to ensure proper prop flow
      this.$nextTick(() => {
        this.showEbmDataEditor = true;
        console.log(
          "StoreCatalogView: Modal opened with selectedInventoryItem:",
          {
            hasProduct: !!this.selectedInventoryItem?.product,
            productId: this.selectedInventoryItem?.product?.id,
            hasPrice: !!this.selectedInventoryItem?.price,
          },
        );
      });
    },

    handleCloseEbmDataEditor() {
      this.showEbmDataEditor = false;
      this.selectedInventoryItem = null;
    },

    async handleEbmDataSaved(savedData) {
      try {
        console.log("StoreCatalogView: EBM data saved:", savedData);

        // Refetch the specific inventory item to get updated data
        if (this.selectedInventoryItem) {
          const updatedItem = await EbmDataService.refetchInventoryItem(
            this.selectedInventoryItem,
          );

          // Update the inventory array with the refreshed data
          const itemIndex = this.inventory.findIndex(
            (item) => item.id === updatedItem.product.id,
          );

          if (itemIndex !== -1) {
            this.inventory.splice(itemIndex, 1, {
              id: updatedItem.product.id,
              name: updatedItem.product.name,
              sku: updatedItem.product.item_sku || "",
              barcode: updatedItem.product.barcode || "",
              // Keep current row quantity for immediate UX; full refresh happens below
              quantity: this.inventory[itemIndex]?.quantity,
              // Use the field the table prefers for display
              priceFormatted: this.formatPrice(updatedItem.price),
              has_rra_ebm_metadata:
                updatedItem.product.has_rra_ebm_metadata || false,
              // Preserve original structures for modal if needed
              product: updatedItem.product,
              price: updatedItem.price,
              quantityRaw: this.inventory[itemIndex]?.quantityRaw,
              originalData:
                updatedItem.originalData ||
                this.inventory[itemIndex]?.originalData,
            });
          }
        }

        this.showEbmDataEditor = false;
        this.selectedInventoryItem = null;

        // Refresh the current tab to ensure all data is up to date
        this.handleDataChange(this.searchQuery);
      } catch (error) {
        console.error("Error refetching inventory item:", error);
        toast.error(
          "Data updated but failed to refresh display. Please reload the page.",
        );
      }
    },

    handleEbmDataError(error) {
      console.error("EBM data editor error:", error);

      // Extract error message from the structured error object
      let errorMessage = "Failed to update EBM data";

      if (error && typeof error === "object") {
        // Check if it's a structured error object from the modal
        if (error.message) {
          errorMessage = error.message;
        } else if (error.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      }

      toast.error(errorMessage);
    },

    handleEbmValidationError(validationError) {
      console.log("EBM validation error:", validationError);
      toast.error(
        validationError.message || "Please fix validation errors before saving",
      );
    },

    handleEbmDataSuccess(successData) {
      console.log("EBM data success:", successData);

      // Handle structured success data
      if (
        typeof successData === "object" &&
        successData.title &&
        successData.message
      ) {
        toast.success(successData.message, {
          title: successData.title,
          type: successData.type || "success",
        });
      } else if (typeof successData === "string") {
        // Handle simple string success messages
        toast.success(successData);
      }
    },

    // Handle catalog table row clicks
    handleCatalogRowClick({ row, rowIndex, activeTab }) {
      console.log("StoreCatalogView: Catalog table row clicked:", {
        row: row,
        rowIndex: rowIndex,
        activeTab: activeTab,
        selectedStoreId: this.selectedStoreId,
        selectedStoreName: this.selectedStore?.name,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        currentPage: this.currentPage,
        catalogPerPage: this.catalogPerPage,
        totalCatalog: this.totalCatalog,
        rowDetails: {
          id: row.id,
          name: row.name,
          sku: row.sku,
          barcode: row.barcode,
          hasEbmMetadata: row.has_rra_ebm_metadata,
          price: row.priceFormatted || row.price,
          quantity: row.quantity,
        },
      });

      // You can add additional logic here if needed, such as:
      // - Opening a detail view
      // - Selecting the row
      // - Navigating to an edit page
      // - Analytics tracking
    },
  },
  mounted() {
    this.loadStores();
    // Handle initial route parameters
    this.handleRouteChange(this.$route);
  },
};
</script>

<style>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 180ms ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>

<style scoped>
.catalog-page-shell {
  background: transparent;
  min-height: 100vh;
}

.catalog-request-transfer-btn {
  align-items: center;
  background:
    radial-gradient(circle at 88% 0%, rgba(255, 255, 255, 0.34), transparent 34%),
    linear-gradient(135deg, var(--hs-primary, #3293ec), #2563eb);
  border: 1px solid color-mix(in srgb, var(--hs-primary, #3293ec) 44%, transparent);
  border-radius: 16px;
  box-shadow: 0 14px 34px color-mix(in srgb, var(--hs-primary, #3293ec) 28%, transparent);
  color: white;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 0.84rem;
  font-weight: 950;
  gap: 9px;
  min-height: 42px;
  min-width: max-content;
  padding: 0 15px 0 10px;
  transition: box-shadow 0.2s ease, transform 0.2s ease, filter 0.2s ease;
}

.catalog-request-transfer-btn:hover {
  box-shadow: 0 18px 42px color-mix(in srgb, var(--hs-primary, #3293ec) 36%, transparent);
  filter: saturate(1.05);
  transform: translateY(-1px);
}

.catalog-request-transfer-btn__icon {
  align-items: center;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 11px;
  display: flex;
  height: 30px;
  justify-content: center;
  width: 30px;
}

.catalog-action-cluster {
  align-items: center;
  display: inline-flex;
  flex-wrap: nowrap;
  gap: 10px;
  justify-content: flex-end;
  width: auto;
}

.catalog-deficit-count {
  align-items: center;
  background: rgba(220, 38, 38, 0.12);
  border-radius: 999px;
  color: #b91c1c;
  display: inline-flex;
  font-size: 0.7rem;
  font-weight: 950;
  min-height: 20px;
  padding: 0 7px;
}

.catalog-toolbar-custom-select {
  flex: 0 0 220px;
  min-width: 190px;
}

.catalog-toolbar-control,
.catalog-toolbar-date-btn {
  background: var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 15px;
  color: var(--hs-text, #0f172a);
  min-height: 42px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.catalog-toolbar-control {
  cursor: pointer;
  font-size: 0.86rem;
  font-weight: 800;
  min-width: 170px;
  outline: none;
  padding: 0 13px;
}

.catalog-toolbar-control:focus,
.catalog-toolbar-date-btn:focus {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 48%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--hs-primary, #3293ec) 11%, transparent);
}

.catalog-toolbar-date-btn {
  align-items: center;
  display: inline-flex;
  font-size: 0.84rem;
  font-weight: 850;
  gap: 10px;
  justify-content: space-between;
  min-width: 158px;
  padding: 0 12px;
}

.catalog-toolbar-date-btn:hover,
.catalog-toolbar-control:hover {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 26%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.07);
  transform: translateY(-1px);
}

.removal-metrics-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 1rem;
}

.removal-metric-card {
  align-items: center;
  background:
    linear-gradient(145deg,
      color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent),
      color-mix(in srgb, var(--hs-surface, #ffffff) 80%, transparent));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  box-shadow: none;
  display: flex;
  gap: 14px;
  min-height: 96px;
  overflow: hidden;
  padding: 18px;
  position: relative;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.removal-metric-card::before {
  border-radius: 999px;
  content: "";
  height: 96px;
  opacity: 0.14;
  position: absolute;
  right: -34px;
  top: -38px;
  width: 96px;
}

.removal-metric-card:hover {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 24%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: none;
  transform: none;
}

.removal-metric-card__icon {
  align-items: center;
  border-radius: 16px;
  display: flex;
  flex-shrink: 0;
  height: 46px;
  justify-content: center;
  position: relative;
  width: 46px;
  z-index: 1;
}

.removal-metric-card__label {
  color: var(--hs-muted, #64748b);
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  margin: 0;
  text-transform: uppercase;
}

.removal-metric-card__value {
  color: var(--hs-text, #0f172a);
  font-size: clamp(1.25rem, 1.7vw, 1.8rem);
  font-weight: 950;
  line-height: 1.1;
  margin: 7px 0 0;
}

.removal-metric-card--blue::before,
.removal-metric-card--blue .removal-metric-card__icon {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 16%, transparent);
  color: var(--hs-primary, #3293ec);
}

.removal-metric-card--amber::before,
.removal-metric-card--amber .removal-metric-card__icon {
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
}

.removal-metric-card--green::before,
.removal-metric-card--green .removal-metric-card__icon {
  background: rgba(16, 185, 129, 0.15);
  color: #059669;
}

.catalog-summary-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
  min-width: 0;
  width: 100%;
}

.catalog-summary-card {
  background:
    linear-gradient(145deg,
      color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent),
      color-mix(in srgb, var(--hs-surface, #ffffff) 78%, transparent));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  box-shadow: none;
  max-width: 100%;
  min-height: 104px;
  min-width: 0;
  overflow: hidden;
  padding: 16px;
  position: relative;
  text-align: left;
  transition: border-color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease;
}

.catalog-summary-card:hover {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 28%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: none;
  transform: none;
}

.catalog-summary-card__glow {
  border-radius: 999px;
  filter: blur(34px);
  height: 88px;
  opacity: 0.34;
  position: absolute;
  right: -36px;
  top: -42px;
  width: 88px;
}

.catalog-summary-card--blue .catalog-summary-card__glow {
  background: #60a5fa;
}

.catalog-summary-card--green .catalog-summary-card__glow {
  background: #34d399;
}

.catalog-summary-card--purple .catalog-summary-card__glow {
  background: #a78bfa;
}

.catalog-summary-card--amber .catalog-summary-card__glow {
  background: #f59e0b;
  opacity: 0.72;
}

.catalog-summary-card__content {
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 0;
  position: relative;
  z-index: 1;
}

.catalog-summary-card__icon {
  align-items: center;
  border-radius: 14px;
  display: flex;
  flex-shrink: 0;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.catalog-summary-card__label {
  color: var(--hs-muted, #64748b);
  font-size: 0.74rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
}

.catalog-summary-card__value {
  color: var(--hs-text, #0f172a);
  font-size: clamp(1.45rem, 1.8vw, 1.95rem);
  font-weight: 900;
  line-height: 1;
  margin: 6px 0 0;
  overflow-wrap: anywhere;
}

.catalog-summary-card__hint {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.45;
  margin: 6px 0 0;
}

.catalog-summary-card--button {
  align-items: center;
  background:
    radial-gradient(circle at 88% 12%, rgba(251, 191, 36, 0.36), transparent 34%),
    linear-gradient(145deg, #111827, #1f2937);
  border-color: rgba(251, 191, 36, 0.5);
  box-shadow: none;
  color: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  min-width: 0;
  white-space: normal;
  width: 100%;
}

.catalog-summary-card--button:hover {
  border-color: rgba(251, 191, 36, 0.82);
  box-shadow: none;
}

.catalog-summary-card--button .catalog-summary-card__label,
.catalog-summary-card--button .catalog-summary-card__hint {
  color: rgba(255, 255, 255, 0.76);
}

.catalog-summary-card--button .catalog-summary-card__label {
  color: #fde68a;
}

.catalog-summary-card__chevron {
  color: currentColor;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.catalog-summary-card--button :deep(.ui-button__label) {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) auto;
  min-width: 0;
  text-align: left;
  width: 100%;
}

.catalog-summary-card--button :deep(.ui-button__label)>.catalog-summary-card__glow {
  display: none;
}

.catalog-bulk-actions {
  display: grid;
  gap: 14px;
}

.bulk-action-card {
  align-items: center;
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 92%, var(--hs-bg, #f9fafb));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.2));
  border-radius: 20px;
  display: grid;
  gap: 16px;
  grid-template-columns: auto 1fr auto;
  padding: 18px;
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  width: 100%;
  white-space: normal;
}

.bulk-action-card :deep(.ui-button__label) {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: auto 1fr auto;
  text-align: left;
  width: 100%;
}

.bulk-action-card:hover {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 28%, var(--hs-border, rgba(148, 163, 184, 0.2)));
  box-shadow: 0 16px 42px rgba(15, 23, 42, 0.1);
  transform: translateY(-2px);
}

.bulk-action-card--primary {
  background:
    linear-gradient(145deg, color-mix(in srgb, var(--hs-primary, #3293ec) 15%, var(--hs-surface, #ffffff)), var(--hs-surface, #ffffff));
}

.bulk-action-card__icon {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 13%, transparent);
  border-radius: 16px;
  color: var(--hs-primary, #3293ec);
  display: flex;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.bulk-action-card h4 {
  color: var(--hs-text, #0f172a);
  font-size: 1rem;
  font-weight: 900;
  margin: 0;
}

.bulk-action-card p {
  color: var(--hs-muted, #64748b);
  font-size: 0.86rem;
  line-height: 1.55;
  margin-top: 5px;
}

.bulk-action-card__content {
  min-width: 0;
}

.bulk-template-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.bulk-template-row .hs-filter-input {
  min-width: min(220px, 100%);
}

.catalog-page-actions {
  align-items: center;
  background: color-mix(in srgb, var(--hs-surface, #fff) 92%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  box-shadow: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: flex-end;
  padding: 0.85rem;
}

.catalog-packaging-shell {
  display: grid;
  gap: 1rem;
}

.catalog-packaging-header {
  align-items: center;
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem;
}

.catalog-packaging-title {
  color: var(--hs-text, #0f172a);
  font-size: 1rem;
  font-weight: 950;
  margin: 0;
}

.catalog-packaging-description {
  color: var(--hs-muted, #64748b);
  font-size: 0.84rem;
  line-height: 1.5;
  margin: 0.25rem 0 0;
}

.catalog-packaging-actions,
.catalog-packaging-row-actions {
  align-items: center;
  display: flex;
  gap: 0.5rem;
}

.catalog-packaging-row-actions {
  justify-content: center;
  flex-wrap: nowrap;
}

.catalog-packaging-table :deep(table) {
  min-width: 100%;
  table-layout: fixed;
}

.catalog-packaging-table :deep(th:nth-child(1)),
.catalog-packaging-table :deep(td:nth-child(1)) {
  max-width: 12rem;
  width: 12rem;
}

.catalog-packaging-table :deep(th:nth-child(2)),
.catalog-packaging-table :deep(td:nth-child(2)) {
  max-width: 6rem;
  width: 6rem;
}

.catalog-packaging-table :deep(th:nth-child(3)),
.catalog-packaging-table :deep(td:nth-child(3)) {
  max-width: 7rem;
  width: 7rem;
}

.catalog-packaging-table :deep(th:nth-child(4)),
.catalog-packaging-table :deep(td:nth-child(4)) {
  max-width: 16rem;
  white-space: normal;
  width: 16rem;
}

.catalog-packaging-table :deep(td:nth-child(4)) {
  overflow-wrap: anywhere;
}

.catalog-packaging-table :deep(th:nth-child(5)),
.catalog-packaging-table :deep(td:nth-child(5)) {
  min-width: 7.5rem;
  white-space: nowrap;
  width: 7.5rem;
}

.catalog-packaging-name {
  display: grid;
  gap: 0.2rem;
}

.catalog-packaging-name span {
  color: var(--hs-text, #0f172a);
  font-weight: 900;
}

.catalog-packaging-name small {
  color: var(--hs-muted, #64748b);
  font-size: 0.76rem;
  font-weight: 800;
}

.catalog-packaging-pill,
.catalog-packaging-factor {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 900;
  min-height: 1.8rem;
  padding: 0 0.7rem;
}

.catalog-packaging-pill {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 11%, transparent);
  color: var(--hs-primary, #3293ec);
}

.catalog-packaging-factor {
  background: color-mix(in srgb, #10b981 12%, transparent);
  color: #047857;
}

.catalog-packaging-empty {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 55%, transparent);
  border: 1px dashed color-mix(in srgb, var(--hs-border, rgba(148, 163, 184, 0.22)) 80%, transparent);
  border-radius: 14px;
  color: var(--hs-muted, #64748b);
  display: grid;
  gap: 0.85rem;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 7rem;
  padding: 1.2rem;
}

.catalog-packaging-empty p {
  color: var(--hs-text, #0f172a);
  font-weight: 950;
  margin: 0 0 0.25rem;
}

.catalog-packaging-empty span {
  font-size: 0.86rem;
  line-height: 1.5;
}

.catalog-packaging-form {
  display: grid;
  gap: 1rem;
}

.catalog-packaging-form-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.catalog-packaging-form-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

@media (max-width: 1180px) {
  .catalog-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {

  .catalog-action-cluster,
  .catalog-request-transfer-btn,
  .catalog-toolbar-custom-select,
  .catalog-toolbar-control,
  .catalog-toolbar-date-btn {
    width: 100%;
  }

  .catalog-action-cluster {
    align-items: stretch;
    flex-wrap: wrap;
    width: 100%;
  }

  .catalog-summary-grid {
    grid-template-columns: 1fr;
  }

  .catalog-packaging-header,
  .catalog-packaging-form-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .catalog-packaging-actions,
  .catalog-packaging-form-actions>* {
    width: 100%;
  }

  .catalog-packaging-form-grid,
  .catalog-packaging-empty {
    grid-template-columns: 1fr;
  }

  .removal-metrics-grid {
    grid-template-columns: 1fr;
  }

  .catalog-summary-card {
    min-height: 116px;
    padding: 18px;
  }

  .bulk-action-card {
    grid-template-columns: auto 1fr;
  }

  .bulk-action-card>svg {
    display: none;
  }
}
</style>
